import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import moment from 'moment';

import { InputTextModule } from "primeng/inputtext";
import { AutoCompleteModule } from 'primeng/autocomplete';
import { DatePickerModule } from 'primeng/datepicker';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { DynamicForm } from '@ui-nuvertos/shared/dynamic-form/dynamic-form';
import { TabsModule } from 'primeng/tabs';
import { CheckboxModule } from 'primeng/checkbox';
import { ItemDynamicForm } from '../item-dynamic-form/item-dynamic-form';
import { TemplateService } from '@ui-nuvertos/shared/services/template/template.service';
import { ItemService } from '../services/item/item.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ClientOrderService } from '../services/client/client-order.service';
import { ToasterService } from '@ui-nuvertos/auth';
import { ClientOnboardingService } from '../services/client/client-onboarding.service';
import { ClientListService } from '../services/client/client-list.service';
import { MastersService } from '../services/masters/masters.service';
import { TableModule } from 'primeng/table';
import { ClientTypeaheadUtils } from '../utils/client-typeahead.utils';

interface FieldConfig {
  id: number;
  template_id: number;
  field_name: string;
  display_name: string;
  field_type: string;
  data_type: string;
  is_mandatory: boolean;
  is_visible: boolean;
  is_custom_field: boolean;
  tab_id: string | number;
  sub_tab_id: number | string;
  display_order: number;
  validation_rules: string;
  help_text: string;
  default_value: string;
  is_active: boolean;
  options: any[];
  is_deleted: boolean;
  created_by: number;
  updated_by: number;
  created_at: string;
  updated_at: string;
}

@Component({
  selector: 'lib-client-so',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    AutoCompleteModule,
    DatePickerModule,
    RadioButtonModule,
    SelectModule,
    AccordionModule,
    ButtonModule,
    DynamicForm,
    TabsModule,
    CheckboxModule,
    ItemDynamicForm,
    TableModule
  ],
  templateUrl: './client-so.html',
  styleUrl: './client-so.css',
})
export class ClientSo {

  isLoading = false;
  customerForm!: FormGroup;
  data: FieldConfig[] = [];
  dynamicFormTabs: { title: string; value: number; sub_tabs: any }[] = [];
  primaryFields: FieldConfig[] | null = null;
  templates: any;
  masterFields: any;
  customFields: any;
  viewMode: boolean = true;
  mode: string = 'create';
  clientSOId: any = null;
  approvalStatus: string = 'not_required'; // 'pending' | 'approved' | 'rejected' | 'not_required'
  soStatus: string = 'open'; // 'open' | 'partially_complete' | 'closed'
  defaultTemplate: any;
  private tableFormArrays: Map<string, FormArray> = new Map();
  private searchSuggestionsMap: Map<string, any[]> = new Map();
  clientOptions: any[] = [];
  templateOptions: any[] = [];
  currentTemplateId: string | null = null;
  unitsOptions: any[] = [];
  masterData: any = {
    'tax-types': [],
    'tax-rates': [],
    'registration-type-options': [],
    'terms': [],
    'shipping-methods': [],
    'shipping-carriers': [],
    'types': [],
    'directions': [],
    'line-of-businesses': [],
    'registration-types': []
  };
  stateOptions: any[] = [];
  calculatedTaxType: string | null = null;
  currentClientInfo: any = null;
  currentSubsidiaryInfo: any = null;
  private readonly taxOverrideDependentFields: string[] = [
    'registration_type',
    'subsidiary_gst_number',
    'customer_gst_number',
    'registration_no',
    'place_of_supply'
  ];
  private readonly nonEditableFields: string[] = ['order_date', 'client_id', 'form_template_id', 'subsidiary_id', 'location'];
  selectedShippingAddressId: string | null = null;
  selectedSubsidiaryId: string | null = null;
  selectedBillingAddressId: string | null = null;
  selectedLocationId: string | null = null;
  selectedPlaceOfSupplyId: string | null = null;
  subsidiariesData: any[] = [];

  // Audit logs support (view mode only)
  auditLogs: any[] = [];

  tabMasterData: any[] = [
    {
      id: 1,
      name: 'Primary Information',
      is_active: true,
      is_deleted: false,
      created_by: null,
      updated_by: null,
      created_at: '2025-10-30T09:46:52+05:30',
      updated_at: '2025-10-30T09:46:52+05:30',
      sub_tabs: [
        {
          id: 1,
          tab_id: 1,
          name: 'primary information',
          is_active: true,
          is_deleted: false,
          created_by: null,
          updated_by: null,
          created_at: '2025-10-24T09:54:50+05:30',
          updated_at: '2025-10-24T09:54:50+05:30',
          show_heading: false,
          type: 'FIELD'
        }
      ]
    },
    {
      id: 2,
      name: 'Tax Details',
      is_active: true,
      is_deleted: false,
      created_by: null,
      updated_by: null,
      created_at: '2025-10-30T09:46:52+05:30',
      updated_at: '2025-10-30T09:46:52+05:30',
      sub_tabs: [
        {
          id: 2,
          tab_id: 2,
          name: 'tax details',
          is_active: true,
          is_deleted: false,
          created_by: null,
          updated_by: null,
          created_at: '2025-10-24T09:54:51+05:30',
          updated_at: '2025-10-24T09:54:51+05:30',
          show_heading: true,
          type: 'FIELD'
        }
      ]
    },
    {
      id: 3,
      name: 'Items',
      is_active: true,
      is_deleted: false,
      created_by: null,
      updated_by: null,
      created_at: '2025-10-30T09:46:52+05:30',
      updated_at: '2025-10-30T09:46:52+05:30',
      sub_tabs: [
        {
          id: 3,
          tab_id: 3,
          name: 'Items',
          is_active: true,
          is_deleted: false,
          created_by: null,
          updated_by: null,
          created_at: '2025-10-24T09:54:51+05:30',
          updated_at: '2025-10-24T09:54:51+05:30',
          show_heading: true,
          type: 'TABLE'
        },
        {
          id: 5,
          tab_id: 3,
          name: 'Expenses',
          is_active: true,
          is_deleted: false,
          created_by: null,
          updated_by: null,
          created_at: '2025-10-24T09:54:51+05:30',
          updated_at: '2025-10-24T09:54:51+05:30',
          show_heading: true,
          type: 'TABLE'
        }
      ]
    },
    {
      id: 5,
      name: 'Shipping & Billing',
      is_active: true,
      is_deleted: false,
      created_by: null,
      updated_by: null,
      created_at: '2025-10-30T09:46:52+05:30',
      updated_at: '2025-10-30T09:46:52+05:30',
      sub_tabs: [
        {
          id: 7,
          tab_id: 5,
          name: 'Shipping details',
          is_active: true,
          is_deleted: false,
          created_by: null,
          updated_by: null,
          created_at: '2025-10-24T09:54:51+05:30',
          updated_at: '2025-10-24T09:54:51+05:30',
          show_heading: true,
          type: 'FIELD'
        },
        {
          id: 8,
          tab_id: 5,
          name: 'Billing details',
          is_active: true,
          is_deleted: false,
          created_by: null,
          updated_by: null,
          created_at: '2025-10-24T09:54:51+05:30',
          updated_at: '2025-10-24T09:54:51+05:30',
          show_heading: true,
          type: 'FIELD'
        }
      ]
    },
    // {
    //   id: 6,
    //   name: 'Approval',
    //   is_active: true,
    //   is_deleted: false,
    //   created_by: null,
    //   updated_by: null,
    //   created_at: '2025-10-30T09:46:52+05:30',
    //   updated_at: '2025-10-30T09:46:52+05:30',
    //   sub_tabs: [
    //     {
    //       id: 9,
    //       tab_id: 6,
    //       name: 'approval',
    //       is_active: true,
    //       is_deleted: false,
    //       created_by: null,
    //       updated_by: null,
    //       created_at: '2025-10-24T09:54:53+05:30',
    //       updated_at: '2025-10-24T09:54:53+05:30',
    //       show_heading: false,
    //       type: 'FIELD'
    //     }
    //   ]
    // },
    {
      id: 7,
      name: 'Communications',
      is_active: true,
      is_deleted: false,
      created_by: null,
      updated_by: null,
      created_at: '2025-10-30T09:46:52+05:30',
      updated_at: '2025-10-30T09:46:52+05:30',
      sub_tabs: [
        {
          id: 10,
          tab_id: 7,
          name: 'files',
          is_active: true,
          is_deleted: false,
          created_by: null,
          updated_by: null,
          created_at: '2025-10-24T09:54:54+05:30',
          updated_at: '2025-10-24T09:56:01+05:30',
          show_heading: true,
          type: 'TABLE'
        },
        {
          id: 11,
          tab_id: 7,
          name: 'phone calls',
          is_active: true,
          is_deleted: false,
          created_by: null,
          updated_by: null,
          created_at: '2025-10-24T09:54:54+05:30',
          updated_at: '2025-10-24T09:56:01+05:30',
          show_heading: true,
          type: 'TABLE'
        },
        {
          id: 12,
          tab_id: 7,
          name: 'user notes',
          is_active: true,
          is_deleted: false,
          created_by: null,
          updated_by: null,
          created_at: '2025-10-24T09:54:55+05:30',
          updated_at: '2025-10-24T09:56:01+05:30',
          show_heading: true,
          type: 'TABLE'
        }
      ]
    },
    // Audit tab: enabled only in view mode (mirrors onboarding/debit-note pattern)
    {
      id: 8,
      name: 'Audit',
      is_active: true,
      is_deleted: false,
      created_by: null,
      updated_by: null,
      created_at: '2025-10-30T09:46:52+05:30',
      updated_at: '2025-10-30T09:46:52+05:30',
      sub_tabs: [
        {
          id: 13,
          tab_id: 8,
          name: 'audit_logs_filters',
          is_active: true,
          is_deleted: false,
          created_by: null,
          updated_by: null,
          created_at: '2025-10-24T09:54:56+05:30',
          updated_at: '2025-10-24T09:56:01+05:30',
          show_heading: false,
          type: 'FIELD'
        },
        {
          id: 14,
          tab_id: 8,
          name: 'audit_logs',
          is_active: true,
          is_deleted: false,
          created_by: null,
          updated_by: null,
          created_at: '2025-10-24T09:54:56+05:30',
          updated_at: '2025-10-24T09:56:01+05:30',
          show_heading: true,
          type: 'TABLE'
        }
      ]
    },
    // {
    //   id: 9,
    //   name: 'Workflow & Automations',
    //   is_active: true,
    //   is_deleted: false,
    //   created_by: null,
    //   updated_by: null,
    //   created_at: '2025-10-30T09:46:52+05:30',
    //   updated_at: '2025-10-30T09:46:52+05:30',
    //   sub_tabs: [
    //     {
    //       id: 14,
    //       tab_id: 9,
    //       name: 'workflow & automations',
    //       is_active: true,
    //       is_deleted: false,
    //       created_by: null,
    //       updated_by: null,
    //       created_at: '2025-10-24T09:54:57+05:30',
    //       updated_at: '2025-10-24T09:56:01+05:30',
    //       show_heading: true,
    //       type: 'FIELD'
    //     }
    //   ]
    // }
  ];

  tableData: any = 
  [
    {name:"Subtotal" , amount:"0" },
    {name:"Tax Total" , amount:"0" },
  ]

  constructor(
    private fb: FormBuilder,
    private templateService: TemplateService,
    private itemService: ItemService,
    private activatedRoute: ActivatedRoute,
    private clientOrderService: ClientOrderService,
    private toasterService: ToasterService,
    private router: Router,
    private clientService: ClientOnboardingService,
    private clientListService: ClientListService,
    private mastersService: MastersService,
    private cdr: ChangeDetectorRef
  ) { }

  // CLIENT TYPEAHEAD METHODS (similar to vendor-payment pattern)

  /**
   * Ensure client field is configured as autocomplete
   */
  private ensureClientAutocompleteInitialized(): void {
    this.applyToClientFields(field => {
      field.field_type = 'autocomplete';
      if (!field.options) {
        field.options = [];
      }
    });
  }

  /**
   * Update client autocomplete options
   */
  private updateClientAutocompleteOptions(options: any[]): void {
    this.applyToClientFields(field => {
      field.options = options;
    });
    this.cdr.markForCheck();
  }

  /**
   * Apply callback to all client_id fields
   */
  private applyToClientFields(callback: (field: FieldConfig) => void): void {
    const processed = new Set<FieldConfig>();
    const register = (field?: FieldConfig) => {
      if (field && !processed.has(field)) {
        processed.add(field);
        callback(field);
      }
    };
    register(this.primaryFields?.find(f => f.field_name === 'client_id'));
    this.data
      .filter(field => field.field_name === 'client_id')
      .forEach(register);
  }

  /**
   * Handle client search using typeahead utility
   */
  private async handleClientSearch(query: string): Promise<void> {
    const clientKey = this.getFieldKey('client_id');
    const clientControl = this.customerForm?.get(clientKey);
    const currentClientId = clientControl ? this.normalizeClientId(clientControl.value) : null;

    // Use typeahead utility to search clients
    const result = await ClientTypeaheadUtils.searchClients(
      query,
      this.clientListService,
      currentClientId,
      this.clientOptions
    );

    // Update clientOptions array with search results
    this.clientOptions = result.options;

    // Update autocomplete options (show search results)
    const searchOptions = result.options.filter(opt => 
      !currentClientId || String(opt.value) !== String(currentClientId)
    );
    this.updateClientAutocompleteOptions(searchOptions);

    // Ensure control value is a client object, not just an ID
    if (clientControl && result.selectedClient) {
      clientControl.setValue(result.selectedClient, { emitEvent: false });
    }
  }

  /**
   * Normalize client ID from various formats (object, string, number)
   */
  private normalizeClientId(value: any): string | number | null {
    return ClientTypeaheadUtils.normalizeClientId(value);
  }


  async ngOnInit() {
    this.isLoading = true;
    const params = this.activatedRoute.snapshot.queryParams;
    const soId = this.activatedRoute.snapshot.queryParamMap.get('soId');
    if (soId) {
      this.clientSOId = soId;
    }
    if (params['mode']) {
      switch (params['mode']) {
        case 'view':
          this.mode = 'view';
          this.viewMode = true;
          break;
        case 'edit':
          this.mode = 'edit';
          this.viewMode = false;
          break;
        default:
          this.mode = 'create';
          this.viewMode = false;
      }
    } else {
      this.mode = this.clientSOId ? (this.mode == 'view' ? 'view' : 'edit') : 'create';
      this.viewMode = false;
    }
    await this.loadTemplateAndData();

    // After initial data load, fetch audit logs in view mode only
    if (this.mode === 'view' && this.clientSOId) {
      await this.loadAuditLogs();
    }
    this.isLoading = false;
  }

  /**
   * Loads the form template and Client data (if editing), then initializes the form with correct values.
   */
  async loadTemplateAndData() {
    try {
      await this.loadClients();
      await this.loadFormTemplates();
      await this.loadMasters();
      await this.loadPackagingMasters();

      if (this.clientSOId) {
        const soResponse = await this.getClientOrderById(this.clientSOId);
        const orderData = soResponse?.data ?? soResponse ?? null;
        if (orderData) {
          // Store approval_status from API response
          this.approvalStatus = orderData?.approval_status || 'not_required';
          
          // Store SO status from API response
          this.soStatus = orderData?.status || 'open';
          
          // Disable editing if order is approved or closed
          if (this.approvalStatus === 'approved' || this.soStatus === 'closed') {
            this.viewMode = true;
            this.mode = 'view';
          }
          
          const formTemplateId = this.normalizeIdValue(orderData?.form_template_id);
          if (formTemplateId) {
            this.currentTemplateId = formTemplateId;
          }

          const templateResponse = await this.fetchTemplateForCurrentSelection();
          if (templateResponse) {
            this.processTemplateResponse(templateResponse);
            await this.populateFormWithOrderData(orderData);
          }
        }
      } else {
        const templateResponse = await this.fetchTemplateForCurrentSelection();
        if (templateResponse) {
          this.processTemplateResponse(templateResponse);
        }
      }
    } catch (err: any) {
      console.error('Error loading template and data:', err);
      this.data = [];
      this.primaryFields = [];
      this.dynamicFormTabs = [];
    }
  }

  private processTemplateResponse(templateResponse: any): void {
    this.customerForm = this.fb.group({})
    this.templates = templateResponse;
    const templateIdFromResponse = this.normalizeIdValue(
      templateResponse?.data?.template?.id ??
        templateResponse?.template?.id ??
        templateResponse?.data?.id
    );
    if (templateIdFromResponse) {
      this.currentTemplateId = templateIdFromResponse;
    }

    const templateData = templateResponse?.data ?? templateResponse ?? null;
    const rawFields = Array.isArray(templateData?.fields) ? templateData.fields : [];
    this.data = rawFields.filter(
      (field: FieldConfig) => !field.is_deleted && field.is_active && field.is_visible
    );

    this.data.forEach(field => {
      if (field.field_name === 'subsidiary_gst_number' || field.field_name === 'customer_gst_number') {
        field.field_type = 'select';
        if (!field.options) {
          field.options = [];
        }
      }
    });

    this.primaryFields = this.data.filter((field: FieldConfig) =>
      this.idsMatch(field.tab_id, 1)
    );
    this.customFields = this.data.filter((field: FieldConfig) => field.is_custom_field && !field.is_deleted && field.is_active && field.tab_id != 3);
    const formTemplateField = [...this.data].find(
      (field: FieldConfig) => field.field_name === 'form_template_id'
    );
    if (formTemplateField) {
      formTemplateField.options = this.getTemplateOptionEntries();
      if (this.currentTemplateId) {
        formTemplateField.default_value = this.currentTemplateId;
      }
    }

    this.initializeForm();
    this.buildDynamicFormTabs();
    this.applyTemplateOptionsToField();
    this.applyClientOptionsToField();
    this.setFormTemplateControlValue(this.currentTemplateId);
    this.bindMastersToFields();
    this.syncTaxOverrideDependentFieldsFromForm();
  }


  /**
   * Initializes the form. If customerData is provided, uses it to set initial values. Otherwise uses field defaults.
   */
  initializeForm(customerData?: any) {
    const formControls: Record<string, any> = {};

    // Always add 'id' field for patching and submission
    formControls['id'] = [customerData?.id ?? ''];

    const addFieldControl = (field: FieldConfig) => {
      if (!field || typeof field.field_name !== 'string') return;
      if (String(field.field_type || '').toLowerCase() === 'table') return;

      const key = this.getFieldKey(field.field_name);
      if (formControls[key]) return;

      const validators = field.is_mandatory ? [Validators.required] : [];
      let value =
        customerData && customerData[key] !== undefined && customerData[key] !== null
          ? customerData[key]
          : this.getFieldDefaultValue(field);

      if (!this.clientSOId && field.field_name === 'order_date' && field.field_type === 'date') {
        value = new Date();
      }

      if (!this.clientSOId && field.field_name === 'order_no' && (!customerData || !customerData[key])) {
        value = 'System Generated';
      }

      if (field.field_name === 'form_template_id') {
        const normalizedValue = this.normalizeIdValue(
          customerData ? customerData[key] : value ?? this.currentTemplateId
        );
        if (!normalizedValue && this.currentTemplateId) {
          value = this.currentTemplateId;
        } else if (normalizedValue) {
          value = normalizedValue;
          this.currentTemplateId = normalizedValue;
        } else {
          const options = this.getTemplateOptionEntries();
          if (options.length > 0) {
            value = options[0].value;
            this.currentTemplateId = options[0].value;
          }
        }
      }

      formControls[key] = [value, validators];
    };

    (this.primaryFields ?? []).forEach(addFieldControl);

    const secondaryFields = (this.data ?? []).filter(
      (field) => !this.idsMatch(field.tab_id, 1)
    );
    secondaryFields.forEach(addFieldControl);

    this.customerForm = this.fb.group(formControls);
    this.initializeTableFormArrays();
    this.disableNonEditableFields();
  }

  private getFieldDefaultValue(field: FieldConfig): any {
    if (field?.field_name === 'tax_type' && this.calculatedTaxType) {
      return this.calculatedTaxType;
    }
    // Add custom logic for specific field types
    switch (field.field_type) {
      case 'checkbox':
        return false;
      case 'number':
        return 0;
      case 'date':
      case 'datetime':
      case 'time':
        return null;
      case 'select':
        return null;
      case 'radio':
        return null;
      default:
        return field.default_value || '';
    }
  }


  private buildDynamicFormTabs(): void {
    if (!Array.isArray(this.tabMasterData)) {
      this.dynamicFormTabs = [];
      return;
    }

    // Exclude tab with id 1 (Primary Information)
    let filteredTabs = (this.tabMasterData || []).filter((tab: any) => !this.idsMatch(tab?.id, 1));

    // Only show Audit tab (id 8) in view mode
    if (this.mode !== 'view') {
      filteredTabs = filteredTabs.filter((tab: any) => tab.id !== 8);
    }

    const tabs = filteredTabs.map((tab: any, tabIndex: number) => {
      const subTabs = (tab.sub_tabs || []).map((subTab: any, subIndex: number) => {
        const fieldsForSubTab = this.data.filter((field: FieldConfig) => {
          const sameTab = this.idsMatch(field.tab_id, tab.id);
          const sameSubTab = this.idsMatch(field.sub_tab_id, subTab.id);
          const isDefaultSubTab =
            (field.sub_tab_id === null ||
              field.sub_tab_id === undefined ||
              Number(field.sub_tab_id) === 0) &&
            subIndex === 0;
          return sameTab && (sameSubTab || isDefaultSubTab);
        });

        const baseKey = this.getFieldKey(subTab.title || subTab.name || `sub_tab_${subTab.id}`);
        const resolvedKey = this.resolveTableControlKey(tab, subTab, baseKey);
        const subTabPayload = {
          id: subTab.id,
          tab_id: subTab.tab_id,
          title: this.capitalize(subTab.name || subTab.title),
          key: resolvedKey,
          show_heading: subTab.show_heading,
          type: (subTab.type || subTab.sub_type || '').toString().toLowerCase(),
          content: fieldsForSubTab,
          // For audit logs table, attach auditData so dynamic-form knows to render it
          auditData:
            tab.id === 8 && (subTab.id === 14 || subTab.name === 'audit_logs')
              ? this.auditLogs
              : null,
          // Allow filters sub-tab (id 13) to be editable in view mode
          editableInViewMode:
            tab.id === 8 && (subTab.id === 13 || subTab.name === 'audit_logs_filters')
        };

        if (subTabPayload.type === 'table') {
          const tableKey = subTabPayload.key;
          const formArray = this.getTableFormArray(tableKey);
          if (formArray.length === 0) {
            const newRow = this.createEmptyTableRow(fieldsForSubTab);
            formArray.push(newRow);
          }
        }

        return subTabPayload;
      });
      return {
        title: tab.name,
        value: tabIndex + 1,
        sub_tabs: subTabs
      };
    });
    this.dynamicFormTabs = tabs;
  }

  /**
   * Load audit logs for the client order (view mode only)
   */
  private async loadAuditLogs(): Promise<void> {
    if (!this.clientSOId || this.mode !== 'view') {
      return;
    }

    try {
      const auditResponse = await lastValueFrom(
        this.clientOrderService.getAuditLogs(this.clientSOId)
      );
      if (auditResponse?.data?.audit_logs) {
        this.auditLogs = auditResponse.data.audit_logs;
        // Rebuild tabs so that audit tab gets auditData bound
        this.buildDynamicFormTabs();
        this.updateAuditLogsFormArray();
      } else if (auditResponse?.audit_logs) {
        this.auditLogs = auditResponse.audit_logs;
        this.buildDynamicFormTabs();
        this.updateAuditLogsFormArray();
      } else {
        this.auditLogs = [];
        this.updateAuditLogsFormArray();
      }
    } catch (error) {
      console.error('Error loading audit logs:', error);
      this.auditLogs = [];
    }
  }

  /**
   * Handle audit log filtering from filter fields in Audit tab
   */
  private async handleAuditLogFiltering(key: string, value: any, event: any): Promise<void> {
    try {
      if (!this.clientSOId) {
        console.warn('No client SO ID available for filtering');
        return;
      }

      const filterParams: any = {};

      // Normalize key to handle different naming styles
      const normalizedKey = key.toLowerCase().replace(/[^a-z0-9]/g, '_');

      if ((normalizedKey === 'audit_date' || normalizedKey.includes('date')) && value) {
        const dateValue =
          value instanceof Date ? moment(value).format('YYYY-MM-DD') : value;
        filterParams.date = dateValue;
      }

      const response = await lastValueFrom(
        this.clientOrderService.getAuditLogs(this.clientSOId, filterParams)
      );

      if (response?.data?.audit_logs) {
        this.auditLogs = response.data.audit_logs;
        this.updateAuditLogsFormArray();
      } else if (response?.audit_logs) {
        this.auditLogs = response.audit_logs;
        this.updateAuditLogsFormArray();
      } else {
        this.auditLogs = [];
        this.updateAuditLogsFormArray();
      }
    } catch (error) {
      console.error('Error filtering audit logs:', error);
    }
  }

  /**
   * Update audit logs FormArray when audit data changes
   */
  private updateAuditLogsFormArray(): void {
    if (!this.customerForm) {
      return;
    }

    for (const tab of this.dynamicFormTabs) {
      for (const subTab of tab.sub_tabs) {
        if (subTab.auditData) {
          const tableKey = subTab.key || this.getFieldKey(subTab.title);
          let existingArray = this.customerForm.get(tableKey) as FormArray;

          // Create FormArray if it does not exist yet
          if (!existingArray) {
            existingArray = this.fb.array([]);
            this.customerForm.addControl(tableKey, existingArray);
          }

          // Clear existing rows
          while (existingArray.length > 0) {
            existingArray.removeAt(0);
          }

          if (this.auditLogs && this.auditLogs.length > 0) {
            this.auditLogs.forEach((auditLog: any) => {
              const rowGroup = this.fb.group({});

              Object.keys(auditLog).forEach(key => {
                rowGroup.addControl(this.getFieldKey(key), this.fb.control(auditLog[key]));
              });

              existingArray.push(rowGroup);
            });
          }

          this.cdr.detectChanges();
          break;
        }
      }
    }
  }

  private resolveTableControlKey(tab: any, subTab: any, fallbackKey: string): string {
    const subTabType = (subTab?.type || subTab?.sub_type || '').toString().toLowerCase();
    if (subTabType !== 'table') {
      return fallbackKey;
    }

    const tableField = (this.data || []).find(
      (field: FieldConfig) =>
        this.idsMatch(field.tab_id, tab?.id) &&
        this.idsMatch(field.sub_tab_id, subTab?.id) &&
        String(field.field_type || '').toLowerCase() === 'table'
    );

    if (tableField?.field_name) {
      return this.getFieldKey(tableField.field_name);
    }

    if (subTab?.id !== undefined) {
      return this.getFieldKey(`sub_tab_${subTab.id}`);
    }

    return fallbackKey;
  }

  // getFormTemplate and old initializeForm are now handled by loadTemplateAndData
  getFieldKey(fieldName: string): string {
    return fieldName.toLowerCase().replace(/[^a-z0-9]/g, '_');
  }

  getSuggestionsMapForTable(tableKey: string): Map<string, any[]> {
    const filtered = new Map<string, any[]>();
    if (!tableKey) {
      return filtered;
    }

    const prefix = `${tableKey}_`;
    this.searchSuggestionsMap.forEach((value, key) => {
      if (key.startsWith(prefix)) {
        filtered.set(key.substring(prefix.length), value);
      }
    });
    return filtered;
  }

  async onSearchQuery(
    event: { fieldName: string; query: string; rowIndex: number; rowId?: any },
    subTab: any
  ) {
    if (!event || !event.fieldName) {
      return;
    }
    const { fieldName, query, rowIndex } = event;
    const tableKey = subTab?.key || this.getFieldKey(subTab?.title || subTab?.name || '');
    const tableTitle = subTab?.title || this.getFieldKey(subTab?.name || '');
    if (!query || !tableKey) {
      const key = `${tableKey}_${rowIndex}_${fieldName}`;
      this.searchSuggestionsMap.set(key, []);
      this.searchSuggestionsMap = new Map(this.searchSuggestionsMap);
      return;
    }

    try {
      const response = await lastValueFrom(
        this.itemService.searchItems({
          name: query.trim(),
          limit: 20,
          is_deleted: false
        })
      );

      const items = response?.data?.items || response?.data?.item_masters || [];
      const options = items.map((item: any) => ({
        label: item.item_master_name || item.name || item.item_name || item.code || String(item.id || ''),
        value: String(item.id || item.item_id || ''),
        id: item.id,
        name: item.item_master_name || item.name || item.item_name,
        description: item.item_master_description || item.description,
        item_master_name: item.item_master_name,
        item_master_description: item.item_master_description,
        tax_rate: item?.gst_percentage,
        item_master_id: item.item_master_id,
        ...item
      }));

      const key = `${tableKey}_${rowIndex}_${fieldName}`;
      this.searchSuggestionsMap.set(key, options);
      this.searchSuggestionsMap = new Map(this.searchSuggestionsMap);
    } catch (error) {
      const key = `${tableKey}_${rowIndex}_${fieldName}`;
      this.searchSuggestionsMap.set(key, []);
      this.searchSuggestionsMap = new Map(this.searchSuggestionsMap);
      console.error('Error fetching item suggestions:', error);
    }
  }

  getTableFormArray(key: string): FormArray {
    if (!key) return this.fb.array([]);

    let existing = this.customerForm?.get(key);
    if (existing && existing instanceof FormArray) {
      if (!this.tableFormArrays.has(key)) {
        this.tableFormArrays.set(key, existing);
      }
      return existing;
    }

    if (this.tableFormArrays.has(key)) {
      const memoized = this.tableFormArrays.get(key)!;
      if (this.customerForm && !this.customerForm.get(key)) {
        this.customerForm.addControl(key, memoized);
      }
      return memoized;
    }

    const formArray = this.fb.array([]);
    this.tableFormArrays.set(key, formArray);
    if (this.customerForm) {
      this.customerForm.addControl(key, formArray);
    }
    return formArray;
  }

  private async populateFormWithOrderData(orderData: any): Promise<void> {
    if (!orderData || !this.customerForm) {
      return;
    }

    const flattenedOrder = this.flattenOrderData(orderData);
    if (flattenedOrder) {
      const templateKey = this.getFieldKey('form_template_id');
      const flattenedTemplateId = flattenedOrder[templateKey];
      if (flattenedTemplateId) {
        this.currentTemplateId = this.normalizeIdValue(flattenedTemplateId);
      }
    }
    if (Object.keys(flattenedOrder).length > 0) {
      this.customerForm.patchValue(flattenedOrder, { emitEvent: false });
    }

    this.populateOrderItems(orderData?.order_items || []);
    this.populateUserNotes(orderData?.order_notes || []);

    this.customerForm.updateValueAndValidity({ emitEvent: false });
    this.syncTaxOverrideDependentFieldsFromForm();
    this.disableNonEditableFields();

    const clientFieldKey = this.getFieldKey('client_id');
    const clientIdFromForm =
      this.customerForm.get(clientFieldKey)?.value ?? orderData?.client_id;
    if (clientIdFromForm) {
      await this.handleClientChange(clientIdFromForm, true);
    }
  }

  private disableNonEditableFields(): void {
    if (!this.customerForm || this.mode !== 'edit') {
      return;
    }

    this.nonEditableFields.forEach(fieldName => {
      const control = this.customerForm.get(this.getFieldKey(fieldName));
      if (control) {
        control.disable({ emitEvent: false });
      }
    });
  }

  private flattenOrderData(orderData: any): Record<string, any> {
    const flattened: Record<string, any> = {};
    const assign = (fieldName: string, value: any, transform?: (val: any) => any) => {
      const key = this.getFieldKey(fieldName);
      if (!this.customerForm?.get(key)) {
        return;
      }
      const transformedValue = transform ? transform(value) : value;
      if (transformedValue === undefined) {
        return;
      }
      flattened[key] = transformedValue;
    };

    assign('id', orderData?.id ?? '');
    assign('client_id', orderData?.client_id, (val) =>
      val !== undefined && val !== null ? String(val) : null
    );
    assign('order_no', orderData?.order_no ?? '');
    assign('order_date', orderData?.order_date, (val) => this.parseDate(val));
    assign('memo', orderData?.memo ?? '');
    assign('form_template_id', orderData?.form_template_id ?? null);
    assign('place_of_supply', this.toNumberValue(orderData?.place_of_supply_id) ?? null);
    const orderDetails = orderData?.order_details ?? {};
    assign('subsidiary_gst_number', orderDetails?.subsidiary_gst_number ?? '');
    assign('customer_gst_number', orderDetails?.customer_gst_number ?? '');
    assign('registration_type', orderDetails?.registration_type ?? null);
    assign('registration_no', orderDetails?.registration_no ?? '');
    assign('tax_details_override', orderDetails?.tax_details_override ?? false, (val) =>
      Boolean(val)
    );
    assign('terms', orderDetails?.terms ?? '');
    assign('courier_name', orderDetails?.courier_name ?? '');
    assign('shipping_date', orderDetails?.shipping_date, (val) => this.parseDate(val));
    assign('shipping_carrier', orderDetails?.shipping_carrier ?? '');
    assign('shipping_method', orderDetails?.shipping_method ?? '');
    assign('ship_to', orderDetails?.ship_to_id ?? null);
    assign('bill_to', orderDetails?.billing_address ?? null);
    this.selectedSubsidiaryId = orderData?.subsidiary_id ?? null;
    this.selectedLocationId = orderData?.location ?? null;
    this.selectedPlaceOfSupplyId = orderData?.place_of_supply_id ?? null;
    this.selectedShippingAddressId = orderData?.ship_to_id ?? null;
    this.selectedBillingAddressId = orderDetails?.billing_address ?? null;

    const customFieldValues = orderData?.custom_fields ?? {};
    if (customFieldValues && typeof customFieldValues === 'object' && Array.isArray(this.customFields)) {
      this.customFields.forEach((field: FieldConfig) => {
        const fieldName = field?.field_name;
        if (!fieldName) {
          return;
        }
        assign(fieldName, customFieldValues[fieldName]);
      });
    }
    return flattened;
  }

  private parseDate(value: any): Date | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  private populateOrderItems(orderItems: any[]): void {
    const itemsConfig = this.getItemsTableConfig();
    if (!itemsConfig) {
      return;
    }

    const itemsArray = this.getTableFormArray(itemsConfig.key);
    this.clearFormArray(itemsArray);

    if (!Array.isArray(orderItems) || orderItems.length === 0) {
      if (itemsArray.length === 0) {
        const emptyRow = this.createEmptyTableRow(itemsConfig.fields);
        itemsArray.push(emptyRow);
      }
      itemsArray.updateValueAndValidity({ emitEvent: false });
      return;
    }

    orderItems.forEach((item: any) => {
      const initialData = this.buildItemRowInitialData(item);
      const rowGroup = this.createTableRowFromFields(itemsConfig.fields, initialData);
      if (rowGroup.get('id')) {
        rowGroup.patchValue({ id: item?.id ? String(item.id) : '' }, { emitEvent: false });
      }
      if (rowGroup.get('item_id')) {
        rowGroup.patchValue({ item_id: item?.item_id ? String(item.item_id) : '' }, { emitEvent: false });
      }
      if (rowGroup.get('is_deleted')) {
        rowGroup.patchValue({ is_deleted: item?.is_deleted ?? false }, { emitEvent: false });
      }
      itemsArray.push(rowGroup);
      
    });
    itemsArray.updateValueAndValidity({ emitEvent: false });
    this.tableFormArrays.set(itemsConfig.key, itemsArray);
    this.triggerInitialCalculationsForItems(itemsConfig.key, itemsArray);
  }

  private populateUserNotes(userNotes: any[]): void {
    const userNotesConfig = this.getUserNotesTableConfig();
    if (!userNotesConfig) {
      return;
    }

    const notesArray = this.getTableFormArray(userNotesConfig.key);
    this.clearFormArray(notesArray);

    if (!Array.isArray(userNotes) || userNotes.length === 0) {
      if (notesArray.length === 0) {
        const emptyRow = this.createEmptyTableRow(userNotesConfig.fields);
        notesArray.push(emptyRow);
      }
      notesArray.updateValueAndValidity({ emitEvent: false });
      return;
    }
    userNotes.forEach((note: any) => {
      const initialData = this.buildUserNoteRowInitialData(note);
      const rowGroup = this.createTableRowFromFields(userNotesConfig.fields, initialData);
      if (rowGroup.get('id')) {
        rowGroup.patchValue({ id: note?.id ? String(note.id) : '' }, { emitEvent: false });
      }
      if (rowGroup.get('is_deleted')) {
        rowGroup.patchValue({ is_deleted: note?.is_deleted ?? false }, { emitEvent: false });
      }
      notesArray.push(rowGroup);
    });
    notesArray.updateValueAndValidity({ emitEvent: false });
    this.tableFormArrays.set(userNotesConfig.key, notesArray);
  }

  private getItemsTableConfig(): { key: string; fields: FieldConfig[] } | null {
    if (!Array.isArray(this.dynamicFormTabs)) {
      return null;
    }

    for (const tab of this.dynamicFormTabs) {
      if (!Array.isArray(tab?.sub_tabs)) {
        continue;
      }
      for (const subTab of tab.sub_tabs) {
        if ((subTab?.type || '').toString().toLowerCase() !== 'table') {
          continue;
        }
        const fields: FieldConfig[] = subTab?.content || [];
        const hasItemField = fields.some(
          (field: FieldConfig) =>
            field?.field_name === 'item_id' ||
            this.getFieldKey(field?.field_name || '') === this.getFieldKey('item_id')
        );
        if (hasItemField || (subTab?.title && subTab.title.toLowerCase().includes('item'))) {
          const key = subTab?.key || this.getFieldKey(subTab?.title || subTab?.name || '');
          return { key, fields };
        }
      }
    }

    return null;
  }

  private getUserNotesTableConfig(): { key: string; fields: FieldConfig[] } | null {
    if (!Array.isArray(this.dynamicFormTabs)) {
      return null;
    }

    for (const tab of this.dynamicFormTabs) {
      if (!Array.isArray(tab?.sub_tabs)) {
        continue;
      }
      for (const subTab of tab.sub_tabs) {
        if ((subTab?.type || '').toString().toLowerCase() !== 'table') {
          continue;
        }
        // Check if this is the user notes table by sub_tab id or title
        if (subTab?.id === 12 || (subTab?.title && subTab.title.toLowerCase().includes('user notes'))) {
          const key = subTab?.key || this.getFieldKey(subTab?.title || subTab?.name || '');
          const fields: FieldConfig[] = subTab?.content || [];
          return { key, fields };
        }
      }
    }

    return null;
  }

  private clearFormArray(formArray: FormArray | null): void {
    if (!formArray) {
      return;
    }
    while (formArray.length > 0) {
      formArray.removeAt(formArray.length - 1);
    }
  }

  private triggerInitialCalculationsForItems(tableKey: string, tableArray: FormArray): void {
    if (!tableArray || tableArray.length === 0) {
      return;
    }

    tableArray.controls.forEach((control, index) => {
      if (!(control instanceof FormGroup)) {
        return;
      }
      this.calculateItemsTable({
        key: tableKey,
        rowIndex: index,
        fieldName: 'initial_load'
      });
      this.updateItemsSummary();
    });
  }

  private updateItemsSummary() {
    const totals = this.calculateTotals();
    this.tableData = [
      { name: "Gross Amount", amount: totals.grossAmount.toString() },
      { name: "Discount Amount", amount: totals.discountAmount.toString() },
      { name: "Tax Amount", amount: totals.taxAmount.toString() },
      { name: "Total", amount: totals.finalAmount.toString() }
    ];
    
    // Trigger change detection to update summary display
    this.cdr.detectChanges();
  }

  private buildItemRowInitialData(item: any): Record<string, any> {
    const initialData: Record<string, any> = {};
    if (!item) {
      return initialData;
    }

    const assign = (fieldName: string, value: any, transform?: (val: any) => any) => {
      const key = this.getFieldKey(fieldName);
      const transformedValue = transform ? transform(value) : value;
      if (transformedValue === undefined) {
        return;
      }
      initialData[key] = transformedValue;
    };
    assign('item_id', item?.item_id);
    assign('item_name', item?.item_name);
    assign('item_type', item?.item_type);
    assign('quantity', this.toNumberValue(item?.quantity));
    assign('units', item?.unit_id);
    assign('last_purchase_price', this.toNumberValue(item?.last_order_price));
    assign('current_stock', this.toNumberValue(item?.current_stock));
    assign('mrp', this.toNumberValue(item?.mrp));
    assign('rate', this.toNumberValue(item?.rate));
    assign('gross_amount', this.toNumberValue(item?.gross_amount));
    assign('amount', this.toNumberValue(item?.amount));
    assign('tax_rate', this.toNumberValue(item?.tax_rate));
    assign('tax_type', item?.tax_type);
    assign('tax_amount', this.toNumberValue(item?.tax_amount));
    assign('net_amount', this.toNumberValue(item?.net_amount));
    assign('discount_percentage', this.toNumberValue(item?.discount_percentage));
    assign('discount_amount', this.toNumberValue(item?.discount_amount));
    assign('total_amount', this.toNumberValue(item?.total_amount));
    assign('hsn_code', item?.hsn_code);
    assign('uqc', item?.uqc);
    assign('foc', item?.foc);
    assign('pack', item?.pack);
    assign('scheme', item?.scheme);
    assign('description', item?.description);
    assign('line_of_business', item?.line_of_business);

    // Handle custom fields from item.custom_fields object
    if (item?.custom_fields && typeof item.custom_fields === 'object') {
      Object.keys(item.custom_fields).forEach(customFieldName => {
        const value = item.custom_fields[customFieldName];
        if (value !== undefined && value !== null) {
          assign(customFieldName, value);
        }
      });
    }

    return initialData;
  }

  private buildUserNoteRowInitialData(note: any): Record<string, any> {
    const initialData: Record<string, any> = {};
    if (!note) {
      return initialData;
    }

    const assign = (fieldName: string, value: any, transform?: (val: any) => any) => {
      const key = this.getFieldKey(fieldName);
      const transformedValue = transform ? transform(value) : value;
      if (transformedValue === undefined) {
        return;
      }
      initialData[key] = transformedValue;
    };

    assign('title', note?.title);
    assign('memo', note?.memo);
    assign('date', note?.date, (val) => this.parseDate(val));
    assign('time', note?.time, (val) => this.parseTimeValue(val));
    assign('type', note?.type);
    assign('direction', note?.direction);

    // Handle custom fields from note.custom_fields object if any
    if (note?.custom_fields && typeof note.custom_fields === 'object') {
      Object.keys(note.custom_fields).forEach(customFieldName => {
        const value = note.custom_fields[customFieldName];
        if (value !== undefined && value !== null) {
          assign(customFieldName, value);
        }
      });
    }

    return initialData;
  }

  private parseTimeValue(value: any): any {
    if (!value) {
      return null;
    }
    
    // If it's already a Date object, return it as is
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }
    
    // If it's a string (ISO datetime from backend), parse it to Date
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }
      
      // Try to parse as datetime (ISO format from backend)
      const parsed = new Date(trimmed);
      if (!isNaN(parsed.getTime())) {
        return parsed; // Return as Date object for the datetime picker
      }
      
      return null;
    }
    
    return null;
  }

  private initializeTableFormArrays(): void {
    if (!this.customerForm) return;

    const tableFields = (this.data ?? []).filter(
      (field) => String(field.field_type || '').toLowerCase() === 'table'
    );

    tableFields.forEach((field) => {
      const key = this.getFieldKey(field.field_name);
      if (!this.customerForm!.get(key)) {
        this.customerForm!.addControl(key, this.fb.array([]));
      }
      this.tableFormArrays.set(key, this.customerForm!.get(key) as FormArray);
    });
  }

  private createEmptyTableRow(fields: FieldConfig[]): FormGroup {
    const row = this.fb.group({});
    fields.forEach((field) => {
      if (String(field.field_type || '').toLowerCase() === 'table') return;
      const key = this.getFieldKey(field.field_name);
      const validators = field.is_mandatory ? [Validators.required] : [];
      row.addControl(key, this.fb.control(this.getFieldDefaultValue(field), validators));
    });
    return row;
  }

  private createTableRowFromFields(fields: FieldConfig[], initialData: Record<string, any>): FormGroup {
    const row = this.createEmptyTableRow(fields);

    if (!row.get('id')) {
      row.addControl('id', this.fb.control(''));
    }
    if (!row.get('is_deleted')) {
      row.addControl('is_deleted', this.fb.control(false));
    }
    if (!row.get('item_id')) {
      row.addControl('item_id', this.fb.control(false));
    }

    if (initialData && Object.keys(initialData).length > 0) {
      row.patchValue(initialData, { emitEvent: false });
    }

    return row;
  }

  private idsMatch(source: string | number | undefined, target: string | number | undefined): boolean {
    if (source === null || source === undefined || target === null || target === undefined) {
      return false;
    }
    return Number(source) === Number(target);
  }

  private capitalize(value: string | undefined): string {
    if (!value) {
      return '';
    }
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  async onTemplateChange(value: any) {
    if(!value){
      return;
    }
    try {
      this.isLoading = true;

      // Fetch new template based on selected ID
      const newTemplateResp = await lastValueFrom(
        this.templateService.getSOFormTemplateById(value)
      );

      if (newTemplateResp?.data) {
        // Update the response with new template data
        this.processTemplateResponse(newTemplateResp);
        this.data = newTemplateResp.data.fields.filter(
          (field: FieldConfig) => !field.is_deleted && field.is_active && field.is_visible
        );
      }
      this.updateItemsSummary();
      this.isLoading = false;
    } catch (err) {
      console.error('Error fetching new template:', err);
      this.isLoading = false;
    }
  }

  async getClientOrderById(orderId: string | number) {
    const response = await lastValueFrom(this.clientOrderService.getClientOrderById(orderId));
    const order = response?.data ?? response;
    return order;
  }

  getApprovalStatusLabel(status: string): string {
    const labels: { [key: string]: string } = {
      'pending': 'Pending Approval',
      'approved': 'Approved',
      'rejected': 'Rejected',
      'not_required': 'No Approval Required'
    };
    return labels[status] || status;
  }

  getApprovalStatusClass(status: string): string {
    const classes: { [key: string]: string } = {
      'pending': 'warning',
      'approved': 'success',
      'rejected': 'danger',
      'not_required': 'secondary'
    };
    return classes[status] || 'secondary';
  }

  getSOStatusLabel(status: string): string {
    const labels: { [key: string]: string } = {
      'open': 'Open',
      'partially_complete': 'Partially Complete',
      'closed': 'Closed'
    };
    return labels[status] || 'Open';
  }

  async onSubmit() {
    if (!this.customerForm) {
      return;
    }

    // Prevent submission if order is approved or closed
    if (this.clientSOId && this.approvalStatus === 'approved') {
      this.toasterService.warn('Warning', 'Cannot edit sales order with approved status');
      return;
    }
    if (this.clientSOId && this.soStatus === 'closed') {
      this.toasterService.warn('Warning', 'Cannot edit sales order with closed status');
      return;
    }

    const payload = this.buildClientOrderPayload();
    const isCreate = !this.clientSOId;
    this.isLoading = true;
    try {
      let response;
      if (isCreate) {
        response = await lastValueFrom(this.clientOrderService.createClientOrder(payload));
      } else {
        response = await lastValueFrom(
          this.clientOrderService.updateClientOrder(String(this.clientSOId), payload)
        );
      }

      // Update approval status from response if available
      if (response?.data?.approval_status) {
        this.approvalStatus = response.data.approval_status;
      }
      
      // Update SO status from response if available
      if (response?.data?.status) {
        this.soStatus = response.data.status;
      }
      
      if (isCreate) {
        this.toasterService.showMessage(
          this.toasterService.MESSAGE_TYPES.SUCCESS,
          'Client order created successfully.'
        );
      } else {
        this.toasterService.showMessage(
          this.toasterService.MESSAGE_TYPES.SUCCESS,
          'Client order updated successfully.'
        );
      }
      if(response){
        await this.router.navigate(['/clients/sales-order/list']);
      }
    } catch (error: any) {
      const message =
        error?.error?.error ??
        error?.error?.message ??
        error?.message ??
        (isCreate
          ? 'Failed to create client order. Please try again.'
          : 'Failed to update client order. Please try again.');
      this.toasterService.showMessage(this.toasterService.MESSAGE_TYPES.ERROR, message);
    } finally {
      this.isLoading = false;
    }
  }

  onTableFieldChanged(event: any, subTab: any) {
    if (!event || !event.key || event.rowIndex === undefined || event.rowIndex === null) {
      return;
    }
    if (event.fieldName === 'item_name') {
      this.onItemTableFieldChanged(event);
    }

    const tableKey: any = String(subTab?.title)?.toLocaleLowerCase();
    if (Array.isArray(event.value) && this.calculatedTaxType) {
      const formArray = this.tableFormArrays.get(event.key) as FormArray;
      if (formArray) {
        formArray.controls.forEach((control: any, index: number) => {
          if (control instanceof FormGroup) {
            const taxTypeControl = control.get(this.getFieldKey('tax_type'));
            if (Object.keys(event.value[index]).includes('tax_type')) {
              if (taxTypeControl) {
                taxTypeControl.patchValue(this.calculatedTaxType, { emitEvent: false });
              } else {
                control.addControl(this.getFieldKey('tax_type'), this.fb.control(this.calculatedTaxType));
              }
            }
          }
        });
      }
    }
    if (tableKey === 'items') {
      this.calculateItemsTable(event);
    } else if (tableKey === 'services') {
      this.calculateItemsTable(event);
    }
    this.updateItemsSummary();
  }

  private onItemTableFieldChanged(event: any) {
    const tableArray = this.tableFormArrays.get(event.key);
    if (!tableArray || !(tableArray instanceof FormArray)) {
      return;
    }
    const row = tableArray.at(event.rowIndex);
    if (!(row instanceof FormGroup)) {
      return;
    }

    const selection = event?.fieldValue?.value;
    if (!selection) {
      // Clear all item-related fields if no selection
      this.patchRowValue(row, 'description', '');
      this.patchRowValue(row, 'tax_rate', null);
      this.patchRowValue(row, 'item_name', '');
      this.patchRowValue(row, 'hsn_code', '');
      this.patchRowValue(row, 'mrp', null);
      this.patchRowValue(row, 'pack', null);
      this.patchRowValue(row, 'units', null);
      this.patchRowValue(row, 'item_id', null);
      this.patchRowValue(row, 'discount_percentage', null);
      return;
    }
    const description = selection?.item_master_description ?? selection?.description ?? '';
    this.patchRowValue(row, 'description', description);

    // Tax rate
    const tax_rate = selection?.tax_rate ?? selection?.gst_percentage ?? null;
    this.patchRowValue(row, 'tax_rate', tax_rate);

    // Item name
    const name = selection?.item_master_name ?? selection?.name ?? selection?.label ?? '';
    this.patchRowValue(row, 'item_name', name);

    // HSN code
    const hsn = selection?.hsn_code ?? selection?.hsn ?? '';
    this.patchRowValue(row, 'hsn_code', hsn);

    // MRP
    const mrp = selection?.mrp ?? null;
    this.patchRowValue(row, 'mrp', mrp);

    // Discount Percentage (from discount_percent)
    const discountPercent = selection?.discount_percent ?? null;
    this.patchRowValue(row, 'discount_percentage', discountPercent);

    // Pack (from packing_unit)
    const packingUnit = selection?.packing_unit ?? null;
    this.patchRowValue(row, 'pack', packingUnit);

    // Units (from packaging_id)
    const packagingId = selection?.packaging_id ?? null;
    this.patchRowValue(row, 'units', packagingId);

    // Item ID
    const itemId = selection.item_id ?? selection.value ?? selection.code ?? selection.id ?? null;
    if (itemId) {
      const itemIdKey = this.getFieldKey('item_id');
      const existingControl = row.get(itemIdKey);
      if (!existingControl) {
        row.addControl(itemIdKey, this.fb.control(itemId));
      }
      this.patchRowValue(row, 'item_id', itemId);
    } else {
      this.patchRowValue(row, 'item_id', null);
    }
  }

  private patchRowValue(row: FormGroup, fieldName: string, value: any) {
    const control = row.get(this.getFieldKey(fieldName));
    if (!control) {
      return;
    }

    const currentValue = control.value;
    if (this.areValuesEqual(currentValue, value)) {
      return;
    }

    control.patchValue(value, { emitEvent: false });
  }

  private areValuesEqual(a: any, b: any): boolean {
    if (a === b) {
      return true;
    }

    if (a instanceof Date && b instanceof Date) {
      return a.getTime() === b.getTime();
    }

    if (typeof a === 'number' && typeof b === 'number') {
      return Math.abs(a - b) < 0.0001;
    }

    return false;
  }


  /**
 * Calculate values for items table
 * Handles: amount, discount_amount, tax_amount, total_amount calculations
 */
  private calculateItemsTable(event: any) {
    const tableKey = event.key;
    const rowIndex = event.rowIndex;
    const fieldName = event.fieldName;
    const tableArray = this.tableFormArrays.get(tableKey) as FormArray;

    if (!tableArray || !tableArray.controls[rowIndex]) return;
    const rowControl = tableArray.controls[rowIndex] as FormGroup;
    if (!rowControl) return;
    // Get current row values
    const quantity = Number(rowControl.get(this.getFieldKey('quantity'))?.value || 0);
    const mrp = Number(rowControl.get(this.getFieldKey('mrp'))?.value);
    const discountPercentage = Number(rowControl.get(this.getFieldKey('discount_percentage'))?.value || 0);
    const existingDiscountAmount = Number(rowControl.get(this.getFieldKey('discount_amount'))?.value || 0);
    const taxRate = Number(rowControl.get(this.getFieldKey('tax_rate'))?.value || 0);

    // Get row level flags
    const foc = rowControl.get(this.getFieldKey('foc'))?.value === true ||
      rowControl.get(this.getFieldKey('foc'))?.value === 1 ||
      rowControl.get(this.getFieldKey('foc'))?.value === 'true';

    // If FOC (Free of Charge) is true, set all amounts to 0
    let amount = 0;
    if (!foc) {
      // Calculate amount = quantity * mrp (only if not FOC)
      amount = Math.round((quantity * mrp) * 100) / 100;
    }

    // Handle bidirectional discount calculation (skip if FOC)
    let discountAmount = 0;
    let calculatedDiscountPercentage = 0;

    if (foc) {
      // If FOC, all amounts are 0
      discountAmount = 0;
      calculatedDiscountPercentage = 0;
    } else {
      if (fieldName === 'discount_percentage') {
        // If discount_percentage was changed, calculate discount_amount
        if (discountPercentage > 0 && amount > 0) {
          discountAmount = Math.round((amount * discountPercentage / 100) * 100) / 100;
          calculatedDiscountPercentage = discountPercentage;
        } else {
          discountAmount = 0;
          calculatedDiscountPercentage = 0;
        }
      } else if (fieldName === 'discount_amount') {
        // If discount_amount was changed, calculate discount_percentage
        if (existingDiscountAmount > 0 && amount > 0) {
          discountAmount = existingDiscountAmount;
          calculatedDiscountPercentage = Math.round((discountAmount / amount * 100) * 100) / 100;
        } else {
          discountAmount = 0;
          calculatedDiscountPercentage = 0;
        }
      } else {
        // If neither was changed, use existing values and calculate the other if needed
        if (discountPercentage > 0 && amount > 0) {
          // If percentage exists, calculate amount
          discountAmount = Math.round((amount * discountPercentage / 100) * 100) / 100;
          calculatedDiscountPercentage = discountPercentage;
        } else if (existingDiscountAmount > 0 && amount > 0) {
          // If amount exists, calculate percentage
          discountAmount = existingDiscountAmount;
          calculatedDiscountPercentage = Math.round((discountAmount / amount * 100) * 100) / 100;
        } else if (discountPercentage > 0 && amount === 0) {
          // Preserve discount_percentage even if amount is 0 (e.g., when just set from item selection)
          // Don't calculate discount_amount until amount is available
          discountAmount = 0;
          calculatedDiscountPercentage = discountPercentage;
        } else {
          discountAmount = 0;
          calculatedDiscountPercentage = 0;
        }
      }
    }

    // Calculate net_amount = amount - discount_amount
    const netAmount = Math.round((amount - discountAmount) * 100) / 100;

    // Check if GST exempt is enabled at form level
    const gstExemptControl = this.customerForm?.get(this.getFieldKey('gst_exempt'));
    const isGstExempt = this.normalizeBoolean(gstExemptControl?.value);

    // Calculate tax_amount: if FOC or GST exempt, set tax to 0; otherwise calculate normally
    // Note: tax_rate and tax_type remain unchanged when GST exempt is enabled
    const existingTaxAmount = Number(rowControl.get(this.getFieldKey('tax_amount'))?.value || 0);
    let taxAmount = 0;

    if (foc || isGstExempt) {
      // If FOC or GST exempt, tax_amount is 0 (but tax_rate and tax_type remain unchanged)
      taxAmount = 0;
    } else {
      // Normal tax calculation
      if (fieldName === 'tax_rate') {
        // If tax_rate changed, recalculate from tax_rate
        if (taxRate > 0) {
          taxAmount = Math.round((netAmount * taxRate / 100) * 100) / 100;
        } else {
          taxAmount = 0;
        }
      } else if (taxRate > 0 && fieldName !== 'tax_amount') {
        // Calculate from tax_rate if tax_rate was changed
        taxAmount = Math.round((netAmount * taxRate / 100) * 100) / 100;
      } else if (existingTaxAmount > 0 && fieldName === 'tax_amount') {
        // If tax_amount was directly entered, use it
        taxAmount = existingTaxAmount;
      } else {
        // Keep existing tax_amount if no rate provided
        taxAmount = existingTaxAmount;
      }
    }

    // Calculate total_amount = net_amount + tax_amount (before TDS deduction)
    const totalAmountBeforeTds = Math.round((netAmount + taxAmount) * 100) / 100;

    // Calculate TDS amount based on tds_rate and gross_amount (amount before discount)
    // TDS is calculated on gross_amount, not total_amount
    const tdsRate = Number(rowControl.get(this.getFieldKey('tds_rate'))?.value || 0);
    const tdsAmountControl = rowControl.get(this.getFieldKey('tds_amount'));
    const existingTdsAmountValue = tdsAmountControl?.value;
    const existingTdsAmount = existingTdsAmountValue !== null && existingTdsAmountValue !== undefined
      ? Number(existingTdsAmountValue)
      : null;
    let tdsAmount = 0;

    // TDS is only calculated if not FOC is true (or not specified, default to true)
    // Use amount (gross_amount before discount) for TDS calculation
    
    // If TDS shouldn't be calculated, preserve existing value if it was prefilled, otherwise set to 0
    if (fieldName === 'initial_load' && existingTdsAmount !== null && existingTdsAmount !== undefined) {
      tdsAmount = existingTdsAmount;
    } else {
      tdsAmount = 0;
    }
    

    // Calculate final total_amount = net_amount + tax_amount - tds_amount
    const totalAmount = Math.round((totalAmountBeforeTds - tdsAmount) * 100) / 100;

    // Update form controls if they exist
    const amountKey = this.getFieldKey('gross_amount');
    const discountAmountKey = this.getFieldKey('discount_amount');
    const discountPercentageKey = this.getFieldKey('discount_percentage');
    const taxAmountKey = this.getFieldKey('tax_amount');
    const totalAmountKey = this.getFieldKey('total_amount');
    const tdsRateKey = this.getFieldKey('tds_rate');
    const tdsAmountKey = this.getFieldKey('tds_amount');
    const tdsSectionKey = this.getFieldKey('tds_section');

    // Handle FOC changes - if FOC is set to true, reset all amounts and disable calculation fields
    // Also handle initial_load to ensure fields are disabled if FOC is already true
    if (fieldName === 'foc' || fieldName === 'initial_load') {
      // Toggle calculation fields based on FOC status
      this.toggleCalculationFieldsForFoc(rowControl, foc);

      if (foc) {
        // If FOC is true, set all amounts to 0
        if (rowControl.get(amountKey)) {
          rowControl.patchValue({ [amountKey]: 0 }, { emitEvent: false });
        }
        if (rowControl.get(discountAmountKey)) {
          rowControl.patchValue({ [discountAmountKey]: 0 }, { emitEvent: false });
        }
        if (rowControl.get(discountPercentageKey)) {
          rowControl.patchValue({ [discountPercentageKey]: 0 }, { emitEvent: false });
        }
        if (rowControl.get(taxAmountKey)) {
          rowControl.patchValue({ [taxAmountKey]: 0 }, { emitEvent: false });
        }
        if (rowControl.get(totalAmountKey)) {
          rowControl.patchValue({ [totalAmountKey]: 0 }, { emitEvent: false });
        }
        if (rowControl.get(tdsAmountKey)) {
          rowControl.patchValue({ [tdsAmountKey]: 0 }, { emitEvent: false });
        }
        // Also set mrp to 0 if FOC
        const mrpKey = this.getFieldKey('mrp');
        if (rowControl.get(mrpKey)) {
          rowControl.patchValue({ [mrpKey]: 0 }, { emitEvent: false });
        }
      }
    }

    // Only update if field changed is not the calculated field itself (to avoid infinite loops)
    if (fieldName !== 'gross_amount' && fieldName !== 'amount' && rowControl.get(amountKey)) {
      rowControl.patchValue({ [amountKey]: netAmount }, { emitEvent: false });
    }

    // Update discount_amount and discount_percentage bidirectionally
    if (fieldName !== 'discount_amount' && rowControl.get(discountAmountKey)) {
      rowControl.patchValue({ [discountAmountKey]: discountAmount }, { emitEvent: false });
    }
    if (fieldName !== 'discount_percentage' && rowControl.get(discountPercentageKey)) {
      rowControl.patchValue({ [discountPercentageKey]: calculatedDiscountPercentage }, { emitEvent: false });
    }

    // Update tax_amount (will be 0 if FOC)
    if (fieldName !== 'tax_amount' && rowControl.get(taxAmountKey)) {
      rowControl.patchValue({ [taxAmountKey]: taxAmount }, { emitEvent: false });
    }

    if (fieldName !== 'total_amount' && rowControl.get(totalAmountKey)) {
      rowControl.patchValue({ [totalAmountKey]: totalAmount }, { emitEvent: false });
    }

    // Update tds_amount
    if (fieldName !== 'tds_amount' && rowControl.get(tdsAmountKey)) {
      rowControl.patchValue({ [tdsAmountKey]: tdsAmount }, { emitEvent: false });
    }

    // Note: updateItemsSummary is called in onTableFieldChanged to avoid duplicate calls
  }

  /**
   * Disable/enable calculation fields based on FOC status
   */
  private toggleCalculationFieldsForFoc(rowControl: FormGroup, foc: boolean) {
    const calculationFields = [
      'mrp',
      'last_purchase_price',
      'discount_percentage',
      'discount_amount',
      'tax_rate',
      'tax_amount',
      'tds_rate',
      'tds_amount',
      'tds_section'
    ];

    calculationFields.forEach(fieldName => {
      const fieldKey = this.getFieldKey(fieldName);
      const control = rowControl.get(fieldKey);
      if (control) {
        if (foc) {
          control.disable({ emitEvent: false });
        } else {
          control.enable({ emitEvent: false });
        }
      }
    });
  }

  private normalizeBoolean(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'number') {
      return value === 1;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed === '') {
        return false;
      }
      const normalized = trimmed.toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes';
    }
    return false;
  }

  private setFieldOptions(fieldName: string, options: any[]): void {
    const normalizedOptions = Array.isArray(options) ? options : [];
    const fields = [...(this.primaryFields ?? []), ...(this.data ?? [])];

    fields
      .filter(field => field.field_name === fieldName)
      .forEach(field => {
        field.options = normalizedOptions;
      });

    if (fieldName === 'ship_to' && normalizedOptions.length === 0) {
      this.selectedShippingAddressId = null;
    }

    if (fieldName === 'bill_to' && normalizedOptions.length === 0) {
      this.selectedBillingAddressId = null;
    }
  }

  private getFieldOptions(fieldName: string): any[] {
    const fields = [...(this.primaryFields ?? []), ...(this.data ?? [])];
    for (const field of fields) {
      if (field.field_name === fieldName && Array.isArray(field.options)) {
        return field.options;
      }
    }
    return [];
  }

  private normalizeOptionValue(value: any): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'object') {
      const candidate =
        value.id ?? value.value ?? value.client_id ?? value.name ?? value.label ?? null;
      return candidate !== null && candidate !== undefined ? String(candidate) : null;
    }

    return String(value);
  }

  private findFieldOption(fieldName: string, value: any): any | null {
    const normalizedValue = this.normalizeOptionValue(value);
    if (normalizedValue === null) {
      return null;
    }

    const options = this.getFieldOptions(fieldName);
    return (
      options.find(option => {
        const candidate =
          option?.value ?? option?.id ?? option?.client_id ?? option?.name ?? option?.label;
        return candidate !== undefined && candidate !== null
          ? String(candidate) === normalizedValue
          : false;
      }) ?? null
    );
  }

  private handleShipToSelection(selection: any): void {
    // Only store the ID (bigint converted to string) from the selection
    this.selectedShippingAddressId = this.normalizeIdValue(selection);
  }

  private handleBillToSelection(selection: any): void {
    // Only store the ID (bigint converted to string) from the selection
    this.selectedBillingAddressId = this.normalizeIdValue(selection?.id ?? null);
  }


  private buildClientOrderPayload(): any {
    const formValues = this.customerForm?.getRawValue?.() ?? {};
    const getValue = (field: string, fallback?: any) => this.getFormValue(formValues, field, fallback);
    const getFromFields = (fields: string[], fallback?: any) => {
      for (const field of fields) {
        const candidate = getValue(field);
        if (candidate !== undefined && candidate !== null && candidate !== '') {
          return candidate;
        }
      }
      return fallback;
    };

    const totals = this.calculateTotals();

    const clientOrder = this.pruneNullish(
      {
        client_id: this.normalizeIdValue(getValue('client_id')),
        order_no: this.clientSOId ? this.toStringValue(getValue('order_no')) : null,
        order_date: this.toIsoString(getValue('order_date')),
        memo: this.toStringValue(getValue('memo')),
        form_template_id: this.toStringValue(getValue('form_template_id')),
        location: this.selectedLocationId ?? null,
        subsidiary_id: this.toStringValue(
          getFromFields(['subsidiary_id', 'subsidiary', 'client_subsidiary'])
        ),
        place_of_supply_id: this.normalizeIdValue(
          getFromFields(['place_of_supply_id', 'place_of_supply'])
        ),
        gross_amount: totals.grossAmount,
        discount_amount: totals.discountAmount,
        tax_amount: totals.taxAmount,
        final_amount: totals.finalAmount,
        ship_to_id: this.selectedShippingAddressId ?? null,
      },
      true
    );

    const orderDetails = this.pruneNullish(
      {
        subsidiary_gst_number: this.toStringValue(getValue('subsidiary_gst_number')),
        customer_gst_number: this.toStringValue(getValue('customer_gst_number')),
        registration_type: this.toStringValue(getValue('registration_type')),
        registration_no: this.toStringValue(getValue('registration_no')),
        tax_details_override: this.normalizeBoolean(getValue('tax_details_override')),
        terms: getValue('terms'),
        courier_name: this.toStringValue(getValue('courier_name')),
        shipping_date: this.toIsoString(getValue('shipping_date')),
        shipping_carrier: this.toStringValue(getValue('shipping_carrier')),
        shipping_method: this.toStringValue(getValue('shipping_method')),
        billing_address: this.selectedBillingAddressId ?? null,
      },
      true
    );

    const orderItems = this.buildOrderItems();
    const userNotes = this.buildUserNotes();
    const customFields = this.extractCustomFields(formValues);

    const payload: any = {
      client_order: clientOrder,
      order_details: orderDetails,
      order_items: orderItems,
      user_notes: userNotes,
      custom_fields: customFields
    };

    if (!payload.order_notes?.length) {
      delete payload.order_notes;
    }

    if (!payload.order_files?.length) {
      delete payload.order_files;
    }

    if (!payload.user_notes?.length) {
      delete payload.user_notes;
    }

    if (!payload.custom_fields || Object.keys(payload.custom_fields).length === 0) {
      delete payload.custom_fields;
    }

    return payload;
  }

  private calculateTotals(): {
    grossAmount: number;
    discountAmount: number;
    taxAmount: number;
    finalAmount: number;
  } {
    const itemsArray = this.getTableArray('sub_tab_3');

    if (!itemsArray || itemsArray.length === 0) {
      return {
        grossAmount: 0,
        discountAmount: 0,
        taxAmount: 0,
        finalAmount: 0
      };
    }

    let grossAmount = 0;
    let discountAmount = 0;
    let taxAmount = 0;

    const grossAmountKey = this.getFieldKey('gross_amount');
    const amountKey = this.getFieldKey('amount');
    const discountAmountKey = this.getFieldKey('discount_amount');
    const taxAmountKey = this.getFieldKey('tax_amount');

    itemsArray.controls.forEach(control => {
      if (!(control instanceof FormGroup)) {
        return;
      }

      if (this.isRowMarkedDeleted(control)) {
        return;
      }

      const rowGrossAmount =
        Number(control.get(grossAmountKey)?.value ?? control.get(amountKey)?.value ?? 0);
      const rowDiscountAmount = Number(control.get(discountAmountKey)?.value ?? 0);
      const rowTaxAmount = Number(control.get(taxAmountKey)?.value ?? 0);

      grossAmount += isNaN(rowGrossAmount) ? 0 : rowGrossAmount;
      discountAmount += isNaN(rowDiscountAmount) ? 0 : rowDiscountAmount;
      taxAmount += isNaN(rowTaxAmount) ? 0 : rowTaxAmount;
    });

    grossAmount = Math.round(grossAmount * 100) / 100;
    discountAmount = Math.round(discountAmount * 100) / 100;
    taxAmount = Math.round(taxAmount * 100) / 100;

    const finalAmount =
      Math.round((grossAmount + taxAmount) * 100) / 100;

    return {
      grossAmount,
      discountAmount,
      taxAmount,
      finalAmount
    };
  }

  private async loadClients(): Promise<void> {
    try {
      this.ensureClientAutocompleteInitialized();
      // Set empty options initially - will be populated by search
      this.clientOptions = [];
      this.applyClientOptionsToField();
    } catch (error) {
      console.error('Error initializing client field:', error);
      this.clientOptions = [];
    }
  }

  private async loadFormTemplates(): Promise<void> {
    try {
      const response = await lastValueFrom(this.templateService.getSOFormTemplates());
      const templates = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
        ? response
        : [];
      this.templateOptions = templates;

      const defaultTemplate =
        templates.find((tpl: any) => tpl?.is_default) ?? templates[0] ?? null;
      if (!this.currentTemplateId && defaultTemplate) {
        this.currentTemplateId = this.normalizeIdValue(
          defaultTemplate.id ?? defaultTemplate.template_id ?? defaultTemplate.value
        );
      }
    } catch (error) {
      console.error('Error loading client order form templates:', error);
      this.templateOptions = [];
    }
  }

  private async loadMasters(): Promise<void> {
    try {
      const mastersToLoad = [
        'tax-types',
        'tax-rates',
        'registration-type-options',
        'terms',
        'shipping-methods',
        'shipping-carriers',
        'note-types',
        'directions',
        'line-of-businesses',
        'registration-types',
        'states'
      ];

      const response = await lastValueFrom(
        this.mastersService.getMasters(mastersToLoad)
      );

      const mastersData = response?.data ?? response ?? {};

      // Store master data
      if (mastersData['tax-types']) {
        this.masterData['tax-types'] = Array.isArray(mastersData['tax-types'])
          ? mastersData['tax-types']
          : [];
      }
      if (mastersData['tax-rates']) {
        this.masterData['tax-rates'] = Array.isArray(mastersData['tax-rates'])
          ? mastersData['tax-rates']
          : [];
      }
      if (mastersData['registration-type-options']) {
        this.masterData['registration-type-options'] = Array.isArray(mastersData['registration-type-options'])
          ? mastersData['registration-type-options']
          : [];
      }
      if (mastersData['terms']) {
        this.masterData['terms'] = Array.isArray(mastersData['terms'])
          ? mastersData['terms']
          : [];
      }
      if (mastersData['shipping-methods']) {
        this.masterData['shipping-methods'] = Array.isArray(mastersData['shipping-methods'])
          ? mastersData['shipping-methods']
          : [];
      }
      if (mastersData['shipping-carriers']) {
        this.masterData['shipping-carriers'] = Array.isArray(mastersData['shipping-carriers'])
          ? mastersData['shipping-carriers']
          : [];
      }
      if (mastersData['note-types']) {// TODO make the notes type here
        this.masterData['note-types'] = Array.isArray(mastersData['note-types'])
          ? mastersData['note-types']
          : [];
      }
      if (mastersData['directions']) {
        this.masterData['directions'] = Array.isArray(mastersData['directions'])
          ? mastersData['directions']
          : [];
      }
      if (mastersData['line-of-businesses']) {
        this.masterData['line-of-businesses'] = Array.isArray(mastersData['line-of-businesses'])
          ? mastersData['line-of-businesses']
          : [];
      }
      if (mastersData['registration-types']) {
        this.masterData['registration-types'] = Array.isArray(mastersData['registration-types'])
          ? mastersData['registration-types']
          : [];
      }

      // Load states for place_of_supply
      if (mastersData['states']) {
        const states = Array.isArray(mastersData['states']) 
          ? mastersData['states'] 
          : [];
        this.stateOptions = states.map((item: any) => ({
          ...item,
          id: item.id || item.state_id,
          name: item.state_name,
          value: item.state_code || item.state_id, // Store state_id as value
          label: item.state_name,
          state_code: item.state_code,
          country_code: item.country_code,
        }));
      }

    } catch (error) {
      console.error('Error loading masters:', error);
    }
  }

  private async loadPackagingMasters(): Promise<void> {
    try {
      const response = await lastValueFrom(
        this.mastersService.getPackagingMasters({ limit: 200, is_deleted: false })
      );
      
      const packagingMasters = response?.data?.packaging_masters || response?.data || [];
      
      this.unitsOptions = Array.isArray(packagingMasters)
        ? packagingMasters.map((item: any) => ({
            ...item,
            id: item.id || item._id || item.uuid,
            name: item.name || item.display_name || '',
            value: item.id || item._id || item.uuid,
            label: item.name || item.display_name || '',
            code: item.code || item.id || item._id || item.uuid,
          }))
        : [];
    } catch (error) {
      console.error('Error loading packaging masters:', error);
      this.unitsOptions = [];
    }
  }

  private normalizeUnitsOptions(options: any[]): any[] {
    if (!Array.isArray(options) || options.length === 0) {
      return [];
    }

    return options.map((opt: any) => {
      const label = opt.label ?? opt.name ?? opt.display_name ?? String(opt.id ?? opt.value ?? '');
      const value = opt.value ?? opt.id ?? opt.code ?? label;
      return {
        ...opt,
        id: value,
        value: value,
        label: label,
        name: label,
        raw: opt
      };
    });
  }

  private bindMastersToFields(): void {
    if (!this.data || this.data.length === 0) {
      return;
    }

    // Bind tax-types to tax_type field
    const taxTypeField = this.data.find(
      (field: FieldConfig) => field.field_name === 'tax_type'
    );
    if (taxTypeField && this.masterData['tax-types']) {
      taxTypeField.options = this.transformMasterDataToOptions(
        this.masterData['tax-types'],
        'tax_type'
      );
    }

    // Bind tax-rates to tax_rate field
    const taxRateField = this.data.find(
      (field: FieldConfig) => field.field_name === 'tax_rate'
    );
    if (taxRateField && this.masterData['tax-rates']) {
      taxRateField.options = this.transformMasterDataToOptions(
        this.masterData['tax-rates'],
        'tax_rate'
      );
    }

    // Bind registration-type-options to registration_type field
    const registrationTypeField = this.data.find(
      (field: FieldConfig) => field.field_name === 'registration_type'
    );
    if (registrationTypeField && this.masterData['registration-type-options']) {
      registrationTypeField.options = this.transformMasterDataToOptions(
        this.masterData['registration-type-options'],
        'registration_type'
      );
    }

    // Bind terms to terms field
    const termsField = this.data.find(
      (field: FieldConfig) => field.field_name === 'terms'
    );
    if (termsField && this.masterData['terms']) {
      termsField.options = this.transformMasterDataToOptions(
        this.masterData['terms'],
        'terms'
      );
    }

    // Bind shipping-methods to shipping_method field
    const shippingMethodField = this.data.find(
      (field: FieldConfig) => field.field_name === 'shipping_method'
    );
    if (shippingMethodField && this.masterData['shipping-methods']) {
      shippingMethodField.options = this.transformMasterDataToOptions(
        this.masterData['shipping-methods'],
        'shipping_method'
      );
    }

    // Bind shipping-carriers to shipping_carrier field
    const shippingCarriersField = this.data.find(
      (field: FieldConfig) => field.field_name === 'shipping_carrier'
    );
    if (shippingCarriersField && this.masterData['shipping-carriers']) {
      shippingCarriersField.options = this.transformMasterDataToOptions(
        this.masterData['shipping-carriers'],
        'shipping_carrier'
      );
    }
    const typesField = this.data.find(
      (field: FieldConfig) => field.field_name === 'type'
    );
    if (typesField && this.masterData['note-types']) {
      typesField.options = this.transformMasterDataToOptions(
        this.masterData['note-types'],
        'type'
      );
    }
    const directionsField = this.data.find(
      (field: FieldConfig) => field.field_name === 'direction'
    );
    if (directionsField && this.masterData['directions']) {
      directionsField.options = this.transformMasterDataToOptions(
        this.masterData['directions'],
        'direction'
      );
    }

    // Bind line-of-businesses to line_of_business field (in items table)
    const lineOfBusinessField = this.data.find(
      (field: FieldConfig) => field.field_name === 'line_of_business'
    );
    if (lineOfBusinessField && this.masterData['line-of-businesses']) {
      lineOfBusinessField.options = this.transformMasterDataToOptions(
        this.masterData['line-of-businesses'],
        'line_of_business'
      );
    }
    const registrationField = this.data.find(
      (field: FieldConfig) => field.field_name === 'registration_type'
    );
    if (registrationField && this.masterData['registration-types']) {
      registrationField.options = this.transformMasterDataToOptions(
        this.masterData['registration-types'],
        'registration_type'
      );
    }

    // Bind units options to units field (in items table)
    const unitsField = this.data.find(
      (field: FieldConfig) => field.field_name === 'units'
    );
    if (unitsField && this.unitsOptions.length > 0) {
      unitsField.options = this.normalizeUnitsOptions(this.unitsOptions);
    }

    // Bind place_of_supply options (states)
    const placeOfSupplyField = this.data.find(
      (field: FieldConfig) => field.field_name === 'place_of_supply' || field.field_name === 'place_of_supply_id'
    );
    if (placeOfSupplyField && this.stateOptions.length > 0) {
      placeOfSupplyField.options = this.transformMasterDataToOptions(
        this.stateOptions,
        'place_of_supply'
      );
    }
    
    // Also assign using setFieldOptions to ensure it's available
    if (this.stateOptions.length > 0) {
      this.setFieldOptions('place_of_supply', this.stateOptions);
      this.setFieldOptions('place_of_supply_id', this.stateOptions);
    }
  }

  private transformMasterDataToOptions(masterData: any[], fieldType: string): any[] {
    if (!Array.isArray(masterData) || masterData.length === 0) {
      return [];
    }

    return masterData.map((item: any) => {
      // Handle different master data structures
      let value: any = null;
      let label: string = '';

      value = item.value ?? item.id ?? item.code ?? item.name ?? null;
      label = item.label ?? item.name ?? item.display_name ?? item.code ?? String(value ?? '');

      return {
        id: value,
        value: value,
        label: label,
        name: label,
        raw: item
      };
    });
  }

  private applyClientOptionsToField(): void {
    if (!this.clientOptions.length) {
      return;
    }

    this.setFieldOptions('client_id', this.clientOptions);
  }

  private applyTemplateOptionsToField(): void {
    const options = this.getTemplateOptionEntries();
    if (!options.length) {
      return;
    }

    const templateField = [...(this.primaryFields ?? []), ...(this.data ?? [])].find(
      (field: FieldConfig) => field.field_name === 'form_template_id'
    );
    if (templateField) {
      templateField.options = options;
      if (!templateField.default_value && this.currentTemplateId) {
        templateField.default_value = this.currentTemplateId;
      }
    }

    const control = this.customerForm?.get(this.getFieldKey('form_template_id'));
    if (control) {
      const normalizedValue = this.normalizeIdValue(control.value);
      if (!normalizedValue) {
        const candidate =
          this.currentTemplateId ??
          options.find(opt => opt.value !== undefined && opt.value !== null)?.value ??
          null;
        if (candidate !== null && candidate !== undefined) {
          control.patchValue(candidate, { emitEvent: false });
        }
      }
    }
  }

  private getTemplateOptionEntries(): any[] {
    return (this.templateOptions ?? []).map((tpl: any, index: number) => {
      const id = this.normalizeIdValue(
        tpl?.id ?? tpl?.template_id ?? tpl?.value ?? tpl?.templateId ?? tpl?.uuid
      );
      const label =
        tpl?.name ??
        tpl?.template_name ??
        tpl?.display_name ??
        tpl?.label ??
        `Template ${index + 1}`;
      return {
        id: id ?? label,
        value: id ?? label,
        label,
        name: label,
        raw: tpl
      };
    });
  }

  private setFormTemplateControlValue(templateId: string | null): void {
    if (!this.customerForm) {
      return;
    }
    const control = this.customerForm.get(this.getFieldKey('form_template_id'));
    if (!control) {
      return;
    }
    const normalized = templateId ? String(templateId) : null;
    if (normalized !== null) {
      control.patchValue(normalized, { emitEvent: false });
    } else if (!control.value) {
      control.patchValue(null, { emitEvent: false });
    }
  }

  private async fetchTemplateForCurrentSelection(): Promise<any> {
    if (this.currentTemplateId) {
      try {
        const response = await lastValueFrom(
          this.templateService.getSOFormTemplateById(this.currentTemplateId)
        );
        if (response) {
          return response;
        }
      } catch (error) {
        console.error('Error loading selected template. Falling back to default.', error);
      }
    }

    const fallbackResponse = await lastValueFrom(
      this.templateService.getClientSoDefaultTemplate()
    );
    const fallbackId = this.normalizeIdValue(
      fallbackResponse?.data?.template?.id ??
        fallbackResponse?.template?.id ??
        fallbackResponse?.data?.id
    );
    if (fallbackId) {
      this.currentTemplateId = fallbackId;
    }
    return fallbackResponse;
  }

  private async handleClientChange(clientId: any, initialLoad: boolean = false) {
    const clientCodeKey = this.getFieldKey('client_code');
    const shipToKey = this.getFieldKey('ship_to');
    const clientIdKey = this.getFieldKey('client_id');
    const billToKey = this.getFieldKey('bill_to');
    const registrationNoKey = this.getFieldKey('registration_no');
    const registrationTypeKey = this.getFieldKey('registration_type');
    const customerGstKey = this.getFieldKey('customer_gst_number');
    const subsidiaryGstKey = this.getFieldKey('subsidiary_gst_number');

    if (!clientId) {
      if (initialLoad) {
        this.currentClientInfo = null;
        this.currentSubsidiaryInfo = null;
        this.calculatedTaxType = null;
        this.calculateAndUpdateTaxType();
        return;
      }
      const resetPayload: Record<string, any> = {};
      this.selectedShippingAddressId = null;
      this.selectedBillingAddressId = null;
      this.selectedSubsidiaryId = null;
      this.selectedLocationId = null;
      this.selectedPlaceOfSupplyId = null;

      if (this.customerForm.get(clientCodeKey)) {
        resetPayload[clientCodeKey] = null;
      }
      if (this.customerForm.get(shipToKey)) {
        resetPayload[shipToKey] = null;
      }
      if (this.customerForm.get(clientIdKey)) {
        resetPayload[clientIdKey] = null;
      }
      if (this.customerForm.get(billToKey)) {
        resetPayload[billToKey] = null;
      }
      if (this.customerForm.get(registrationNoKey)) {
        resetPayload[registrationNoKey] = null;
      }
      if (this.customerForm.get(registrationTypeKey)) {
        resetPayload[registrationTypeKey] = null;
      }
      if (this.customerForm.get(customerGstKey)) {
        resetPayload[customerGstKey] = null;
      }
      if (this.customerForm.get(subsidiaryGstKey)) {
        resetPayload[subsidiaryGstKey] = null;
      }

      if (Object.keys(resetPayload).length > 0) {
        this.customerForm.patchValue(resetPayload, { emitEvent: false });
      }

      this.setFieldOptions('ship_to', []);
      this.setFieldOptions('bill_to', []);
      this.setFieldOptions('subsidiary_id', []);
      this.setFieldOptions('location', []);
      this.setFieldOptions('customer_gst_number', []);
      this.setFieldOptions('subsidiary_gst_number', []);
      this.subsidiariesData = [];
      this.currentClientInfo = null;
      this.currentSubsidiaryInfo = null;
      this.calculatedTaxType = null;
      this.calculateAndUpdateTaxType();
      return;
    }

    try {
      const normalizedClientId =
        typeof clientId === 'object'
          ? clientId?.id ?? clientId?.value ?? clientId?.client_id ?? null
          : clientId;

      if (!normalizedClientId) {
        if (initialLoad) {
          this.currentClientInfo = null;
          this.currentSubsidiaryInfo = null;
          this.calculatedTaxType = null;
          this.calculateAndUpdateTaxType();
          return;
        }
        const resetPayload: Record<string, any> = {};
        this.selectedShippingAddressId = null;
        this.selectedBillingAddressId = null;
        this.selectedSubsidiaryId = null;
        this.selectedLocationId = null;
        this.selectedPlaceOfSupplyId = null;
        
        if (this.customerForm.get(clientCodeKey)) {
          resetPayload[clientCodeKey] = null;
        }
        if (this.customerForm.get(shipToKey)) {
          resetPayload[shipToKey] = null;
        }
        if (this.customerForm.get(clientIdKey)) {
          resetPayload[clientIdKey] = null;
        }
        if (this.customerForm.get(billToKey)) {
          resetPayload[billToKey] = null;
        }
        if (this.customerForm.get(registrationNoKey)) {
          resetPayload[registrationNoKey] = null;
        }
        if (this.customerForm.get(registrationTypeKey)) {
          resetPayload[registrationTypeKey] = null;
        }
        if (this.customerForm.get(customerGstKey)) {
          resetPayload[customerGstKey] = null;
        }
        if (this.customerForm.get(subsidiaryGstKey)) {
          resetPayload[subsidiaryGstKey] = null;
        }
        if (Object.keys(resetPayload).length > 0) {
          this.customerForm.patchValue(resetPayload, { emitEvent: false });
        }
        this.setFieldOptions('ship_to', []);
        this.setFieldOptions('bill_to', []);
        this.setFieldOptions('subsidiary_id', []);
        this.setFieldOptions('location', []);
        this.setFieldOptions('customer_gst_number', []);
        this.setFieldOptions('subsidiary_gst_number', []);
        this.subsidiariesData = [];
        this.currentClientInfo = null;
        this.currentSubsidiaryInfo = null;
        this.calculatedTaxType = null;
        this.calculateAndUpdateTaxType();
        return;
      }

      const clientRes: any = await lastValueFrom(
        this.clientService.getClientById(normalizedClientId, true)
      );
      
      // Handle response structure: when include_subsidiary=true, data has both client and subsidiaries
      let clientData = clientRes?.data?.client || clientRes?.data;

      if (!clientData) {
        this.currentClientInfo = null;
        this.calculateAndUpdateTaxType();
        return;
      }

      this.currentClientInfo = clientData;
      const subsidiaries = clientRes?.data?.subsidiaries || [];

    // Populate Client GST options from client tax info
    const clientTaxInfo = clientData.client_tax_info || [];
    const customerGstOptions = Array.isArray(clientTaxInfo)
      ? clientTaxInfo.map((gst: any) => ({
          label: gst.gst_number || '',
          value: gst.gst_number || ''
        }))
      : [];
    
    this.setFieldOptions('customer_gst_number', customerGstOptions);

    // Set default Client GST if available
    const defaultGst = clientTaxInfo.find((tax: any) => tax.mark_as_default);
    if (defaultGst && this.customerForm.get(customerGstKey) && !initialLoad) {
       this.customerForm.patchValue({ [customerGstKey]: defaultGst.gst_number }, { emitEvent: false });
    }

      const clientControl = this.customerForm.get(clientIdKey);
      if (clientControl) {
        // Format the client data into an option object
        const clientOption = ClientTypeaheadUtils.formatClientOption(clientData);
        
        // Update the control value (without emitting event to avoid loop)
        clientControl.setValue(clientOption, { emitEvent: false });
        if(this.mode === 'view') {
          clientControl.setValue(clientOption?.client_name, { emitEvent: false });
        }
        // Ensure this client is in the options list
        const existingIndex = this.clientOptions.findIndex(c => String(c.value) === String(normalizedClientId));
        if (existingIndex >= 0) {
          this.clientOptions[existingIndex] = clientOption;
        } else {
          this.clientOptions.push(clientOption);
        }
      }

      const clientCode = clientData?.client_code ?? clientData?.code ?? '';

      const patchPayload: Record<string, any> = {};

      if (this.customerForm.get(clientCodeKey)) {
        patchPayload[clientCodeKey] = clientCode || null;
      }

      if (this.customerForm.get(registrationNoKey) && !initialLoad) {
        const registrationNumber =
          clientData?.client_basic_details?.registration_number ??
          clientData?.client_basic_details?.registration_no ??
          null;
        patchPayload[registrationNoKey] = registrationNumber ?? null;
      }

      if (this.customerForm.get(registrationTypeKey) && !initialLoad) {
        const registrationType = clientData?.client_basic_details?.registration_type ?? null;
        patchPayload[registrationTypeKey] = registrationType ?? null;
      }

      // Get tax info list for GST and address handling
      const taxInfoList = Array.isArray(clientData?.client_tax_info)
        ? clientData.client_tax_info
        : [];

      if (this.customerForm.get(customerGstKey) && !initialLoad) {
        const defaultTaxInfo =
          taxInfoList.find((info: any) => info?.mark_as_default) ?? taxInfoList[0] ?? null;
        const gstNumber = defaultTaxInfo?.gst_number ?? defaultTaxInfo?.customer_gst_number ?? null;
        patchPayload[customerGstKey] = gstNumber ?? null;
      }

      const billToOptions = taxInfoList
        .filter((info: any) => info?.id && info?.address)
        .map((info: any) => ({
          id: String(info.id),
          value: String(info.id),
          label: info.address // TODO: change this from address to name on column addition at backend
        }));
      this.setFieldOptions('bill_to', billToOptions);

      // Set billing address value - only store the ID
      let billToValue: string | null = null;
      if (initialLoad && this.selectedBillingAddressId) {
        // On initial load, use the stored billing address ID from backend
        billToValue = String(this.selectedBillingAddressId);
      } else if (billToOptions.length > 0) {
        // Otherwise, use the default or first option ID
        const defaultTaxInfo = taxInfoList.find((info: any) => info?.mark_as_default) ?? taxInfoList[0];
        if (defaultTaxInfo?.id) {
          billToValue = String(defaultTaxInfo.id);
          this.selectedBillingAddressId = billToValue;
        }
      }
      
      if (this.customerForm.get(billToKey) && billToValue) {
        patchPayload[billToKey] = billToValue;
      }

      const shipping = clientData?.client_shipping_address ?? {};
      const shipToOptions = shipping?.id && shipping?.shipping_address ? [{
        id: String(shipping.id),
        value: String(shipping.id),
        label: shipping.shipping_address // TODO: change this from address to name on column addition at backend
      }] : [];
      let shipToValue: string | null = null;
      if (initialLoad && this.selectedShippingAddressId) {
        shipToValue = String(this.selectedShippingAddressId);
      } else if (shipToOptions.length > 0 && shipping?.id) {
        shipToValue = String(shipping.id);
        this.selectedShippingAddressId = shipToValue;
      }
      this.setFieldOptions('ship_to', shipToOptions);
      if (this.customerForm.get(shipToKey) && shipToValue) {
        patchPayload[shipToKey] = shipToValue;
      }

      // Fetch and set subsidiary options (pass subsidiaries from API response)
      await this.loadAndSetSubsidiaryOptions(subsidiaries, initialLoad, patchPayload);
      if (Object.keys(patchPayload).length > 0) {
        this.customerForm.patchValue(patchPayload, { emitEvent: false });
      }
      this.calculateAndUpdateTaxType();
    } catch (err: any) {
      this.toasterService.showMessage(this.toasterService.MESSAGE_TYPES.ERROR, err);
      this.currentClientInfo = null;
      this.currentSubsidiaryInfo = null;
      this.calculatedTaxType = null;
      this.calculateAndUpdateTaxType();
    }
  }

  /**
   * Load subsidiaries for the client and set field options
   */
  private async loadAndSetSubsidiaryOptions(
    subsidiaries: any[],
    initialLoad: boolean,
    patchPayload: Record<string, any>
  ): Promise<void> {
    const subsidiaryIdKey = this.getFieldKey('subsidiary_id');
    
    // Use subsidiaries passed from API response
    const subsidiariesArray = Array.isArray(subsidiaries) ? subsidiaries : [];
    
    if (subsidiariesArray.length === 0) {
      // No subsidiaries found, clear options and show message
      this.setFieldOptions('subsidiary_id', []);
      this.setFieldOptions('location', []);
      this.setFieldOptions('place_of_supply', []);
      this.selectedLocationId = null;
      const locationKey = this.getFieldKey('location');
      const subsidiaryGstKey = this.getFieldKey('subsidiary_gst_number');
      const placeOfSupplyKey = this.getFieldKey('place_of_supply');
      if (this.customerForm.get(locationKey)) {
        this.customerForm.patchValue({ [locationKey]: null }, { emitEvent: false });
      }
      if (this.customerForm.get(subsidiaryGstKey)) {
        this.customerForm.patchValue({ [subsidiaryGstKey]: null }, { emitEvent: false });
      }
      if (this.customerForm.get(placeOfSupplyKey)) {
        this.customerForm.patchValue({ [placeOfSupplyKey]: null }, { emitEvent: false });
      }
      this.subsidiariesData = [];
      this.currentSubsidiaryInfo = null;
      this.toasterService.showMessage(
        this.toasterService.MESSAGE_TYPES.WARNING,
        'No subsidiary is linked to this client.'
      );
      this.calculateAndUpdateTaxType();
      return;
    }
    
    // Store full subsidiary data for location handling
    this.subsidiariesData = subsidiariesArray;
    this.currentSubsidiaryInfo = null;
    
    // Create options with only ID and name/label
    const subsidiaryOptions = this.subsidiariesData.map((subsidiary: any) => ({
      id: String(subsidiary.id || subsidiary.subsidiary_id || ''),
      value: String(subsidiary.id || subsidiary.subsidiary_id || ''),
      label: subsidiary.name || 
             subsidiary.subsidiary_name || 
             subsidiary.display_name || 
             String(subsidiary.id || '')
    }));
    
    // Set the options for subsidiary_id field
    this.setFieldOptions('subsidiary_id', subsidiaryOptions);
    
    // Handle subsidiary selection
    if (initialLoad && this.selectedSubsidiaryId && subsidiaryOptions.length > 0) {
      // On initial load, set the subsidiary value and trigger location handling
      patchPayload[subsidiaryIdKey] = String(this.selectedSubsidiaryId);
      // Handle location and place_of_supply for the selected subsidiary on initial load
      await this.handleSubsidiaryChange(String(this.selectedSubsidiaryId), true);
    } else if (!initialLoad && subsidiaryOptions.length > 0) {
      // If no subsidiary is selected initially, clear place_of_supply options
      this.setFieldOptions('place_of_supply', []);
    }
  }

  /**
   * Handle subsidiary change and update location field
   */
  private async handleSubsidiaryChange(subsidiaryId: any, initialLoad: boolean = false): Promise<void> {
    const normalizedSubsidiaryId = this.normalizeIdValue(subsidiaryId);
    
    if (!normalizedSubsidiaryId) {
      // Clear location and place_of_supply options if no subsidiary selected
      this.setFieldOptions('location', []);
      this.setFieldOptions('subsidiary_gst_number', []);
      this.setFieldOptions('place_of_supply', []);
      this.selectedLocationId = null;
      const locationKey = this.getFieldKey('location');
      const subsidiaryGstKey = this.getFieldKey('subsidiary_gst_number');
      const placeOfSupplyKey = this.getFieldKey('place_of_supply');
      if (this.customerForm.get(locationKey)) {
        this.customerForm.patchValue({ [locationKey]: null }, { emitEvent: false });
      }
      if (this.customerForm.get(subsidiaryGstKey)) {
        this.customerForm.patchValue({ [subsidiaryGstKey]: null }, { emitEvent: false });
      }
      this.currentSubsidiaryInfo = null;
      this.calculateAndUpdateTaxType();
      if (this.customerForm.get(placeOfSupplyKey)) {
        this.customerForm.patchValue({ [placeOfSupplyKey]: null }, { emitEvent: false });
      }
      return;
    }

    // Find the selected subsidiary from stored data
    const selectedSubsidiary = this.subsidiariesData.find(
      (sub: any) => String(sub.id || sub.subsidiary_id) === normalizedSubsidiaryId
    );

    if (!selectedSubsidiary) {
      console.warn('Selected subsidiary not found in stored data');
      this.setFieldOptions('location', []);
      this.setFieldOptions('subsidiary_gst_number', []);
      this.setFieldOptions('place_of_supply', []);
      this.selectedLocationId = null;
      this.currentSubsidiaryInfo = null;
      this.calculateAndUpdateTaxType();
      return;
    }
    this.currentSubsidiaryInfo = selectedSubsidiary;

    // Populate subsidiary GST options from tax_information
    const taxInfo = selectedSubsidiary.tax_information || selectedSubsidiary.tax_info || [];
    const subsidiaryGstOptions = Array.isArray(taxInfo)
      ? taxInfo.map((tax: any) => ({
          label: tax.gst_number || '',
          value: tax.gst_number || ''
        }))
      : [];
    
    this.setFieldOptions('subsidiary_gst_number', subsidiaryGstOptions);

    // Extract tax_information addresses from subsidiary for location field
    const taxAddresses = Array.isArray(selectedSubsidiary?.tax_information) && selectedSubsidiary?.tax_information?.length > 0
      ? selectedSubsidiary?.tax_information 
      : [];
    // Create location options from tax_information addresses
    const locationOptions = taxAddresses
      .filter((addr: any) => !addr?.is_deleted && addr?.id && addr?.address)
      .map((addr: any) => ({
        id: String(addr.id),
        value: String(addr.id),
        label: addr.address || `${addr.address_type || 'Address'}`, // Use address string as label
        raw: addr
      }));

    // Determine default address if present (mark_as_default)
    

    // Set the location field options
    this.setFieldOptions('location', locationOptions);

    // Extract addresses from subsidiary for place_of_supply field
    const addresses = Array.isArray(selectedSubsidiary?.addresses) ? selectedSubsidiary.addresses : [];

    const defaultAddress = addresses.find((addr: any) => addr?.mark_as_default) || null;
    
    // Create place_of_supply options from addresses
    const placeOfSupplyOptions = addresses
      .filter((addr: any) => !addr?.is_deleted && addr?.id)
      .map((addr: any) => {
        // Create a formatted label with address and address_type if available
        const addressLabel = addr.address || 
                           (addr.address_type ? `${addr.address_type}` : 'Address');
        const subsidiaryName = selectedSubsidiary.name || selectedSubsidiary.subsidiary_name || '';
        const label = subsidiaryName 
          ? `${subsidiaryName} - ${addressLabel}`
          : addressLabel;
        
        return {
          id: String(addr.id),
          value: String(addr.id),
          label: label,
          address: addr.address,
          address_type: addr.address_type,
          subsidiary_id: String(selectedSubsidiary.id || selectedSubsidiary.subsidiary_id || ''),
          ...addr
        };
      });

    // Set the place_of_supply field options (only addresses from selected subsidiary)
    this.setFieldOptions('place_of_supply', placeOfSupplyOptions);

    // Handle location value setting
    const locationKey = this.getFieldKey('location');
    if (this.customerForm.get(locationKey)) {
      let locationValue: string | null = null;

      if (initialLoad && this.selectedLocationId) {
        // On initial load, use the stored location ID from backend
        locationValue = String(this.selectedLocationId);
        if (locationValue) {
          this.customerForm.patchValue({ [locationKey]: locationValue }, { emitEvent: false });
          // Call handleLocationChange to store the ID but not update GST (initialLoad = true)
          this.handleLocationChange(locationValue, true);
        }
      } else if (initialLoad && !this.selectedLocationId && defaultAddress && defaultAddress.id) {
        // On initial load, if backend didn't provide a selectedLocationId but subsidiary has a default address, prefill it
        locationValue = String(defaultAddress.id);
        this.customerForm.patchValue({ [locationKey]: locationValue }, { emitEvent: false });
        // Use initialLoad=true to avoid overwriting any backend GST value
        this.handleLocationChange(locationValue, true);
      } else if (!initialLoad) {
        //If there's exactly one location option for this subsidiary, auto-select it
        if (Array.isArray(locationOptions) && locationOptions.length === 1) {
          const onlyLocation = locationOptions[0];
          locationValue = onlyLocation?.id ? String(onlyLocation.id) : onlyLocation?.value ? String(onlyLocation.value) : null;
          if (locationValue) {
            this.customerForm.patchValue({ [locationKey]: locationValue }, { emitEvent: false });
            // Trigger location change handler to update GST and tax type
            this.handleLocationChange(locationValue, false);
          }
        } else if (defaultAddress && defaultAddress.id) {
          // If multiple locations exist but one is marked default, preselect that one
          locationValue = String(defaultAddress.id);
          this.customerForm.patchValue({ [locationKey]: locationValue }, { emitEvent: false });
          this.handleLocationChange(locationValue, false);
        } else {
          // Clear location and subsidiary GST when subsidiary changes (not on initial load)
          this.customerForm.patchValue({ [locationKey]: null }, { emitEvent: false });
          this.selectedLocationId = null;

          const subsidiaryGstKey = this.getFieldKey('subsidiary_gst_number');
          if (this.customerForm.get(subsidiaryGstKey)) {
            this.customerForm.patchValue({ [subsidiaryGstKey]: null }, { emitEvent: false });
          }
        }
      }
    }

    // Handle place_of_supply value when subsidiary changes
    const placeOfSupplyKey = this.getFieldKey('place_of_supply');
    if (this.customerForm.get(placeOfSupplyKey)) {
      let placeOfSupplyValue: string | null = null;

      if (initialLoad && this.selectedPlaceOfSupplyId) {
        // On initial load, use the stored place_of_supply_id from backend
        placeOfSupplyValue = String(this.selectedPlaceOfSupplyId);
      } else if (!initialLoad) {
        // When subsidiary changes (not on initial load), check if current value is still valid
        const currentPlaceOfSupplyValue = this.customerForm.get(placeOfSupplyKey)?.value;
        if (currentPlaceOfSupplyValue) {
          // Check if the current value is still valid in the new options
          const isValid = placeOfSupplyOptions.some((opt: any) => 
            String(opt.id) === String(currentPlaceOfSupplyValue) || 
            String(opt.value) === String(currentPlaceOfSupplyValue)
          );
          if (isValid) {
            // Keep the current value if it's still valid
            placeOfSupplyValue = String(currentPlaceOfSupplyValue);
          } else {
            // Clear if the value is not in the new subsidiary's addresses
            placeOfSupplyValue = null;
          }
        }
      }

      if (placeOfSupplyValue !== null) {
        this.customerForm.patchValue({ [placeOfSupplyKey]: placeOfSupplyValue }, { emitEvent: false });
      } else if (!initialLoad) {
        // Clear if not initial load and no valid value
        this.customerForm.patchValue({ [placeOfSupplyKey]: null }, { emitEvent: false });
      }
    }

    // Store the selected subsidiary ID
    this.selectedSubsidiaryId = normalizedSubsidiaryId;
    this.calculateAndUpdateTaxType();
  }

  /**
   * Handle location change and update subsidiary GST number
   */
  private handleLocationChange(locationId: any, initialLoad: boolean = false): void {
    const normalizedLocationId = this.normalizeIdValue(locationId);
    
    // Store the selected location ID
    this.selectedLocationId = normalizedLocationId;

    // Don't update GST on initial load
    if (initialLoad) {
      return;
    }

    if (!normalizedLocationId) {
      // Clear subsidiary_gst_number if no location selected
      const subsidiaryGstKey = this.getFieldKey('subsidiary_gst_number');
      if (this.customerForm.get(subsidiaryGstKey)) {
        this.customerForm.patchValue({ [subsidiaryGstKey]: null }, { emitEvent: false });
      }
      this.calculateAndUpdateTaxType();
      return;
    }

    // Find the location in subsidiariesData
    let foundLocation: any = null;
    
    for (const subsidiary of this.subsidiariesData) {
      const taxInfoList = Array.isArray(subsidiary?.tax_information) 
        ? subsidiary.tax_information 
        : [];
      
      foundLocation = taxInfoList.find(
        (taxInfo: any) => String(taxInfo.id) === normalizedLocationId
      );
      
      if (foundLocation) {
        break;
      }
    }

    if (!foundLocation) {
      console.warn('Selected location not found in subsidiaries data');
      return;
    }

    // Update subsidiary_gst_number with the gst_number from the selected location
    const gstNumber = foundLocation.gst_number || '';
    const subsidiaryGstKey = this.getFieldKey('subsidiary_gst_number');
    
    if (this.customerForm.get(subsidiaryGstKey)) {
      this.customerForm.patchValue({ [subsidiaryGstKey]: gstNumber }, { emitEvent: false });
    }
    this.calculateAndUpdateTaxType();
  }

  private extractStateCodeFromGst(gstNumber: any): string | null {
    if (gstNumber === null || gstNumber === undefined) {
      return null;
    }
    const gstString = String(gstNumber).trim();
    if (gstString.length < 2) {
      return null;
    }
    const code = gstString.substring(0, 2);
    return /^\d{2}$/.test(code) ? code : null;
  }

  private getStateIdFromGstNumber(gstNumber: any, gstType: 'customer' | 'subsidiary'): string | null {
    if (!gstNumber) {
      return null;
    }
    const gstString = String(gstNumber);

    if (gstType === 'customer') {
      const taxInfoList = Array.isArray(this.currentClientInfo?.client_tax_info)
        ? this.currentClientInfo.client_tax_info
        : [];
      const match = taxInfoList.find(
        (tax: any) =>
          !tax?.is_deleted &&
          String(tax?.gst_number ?? tax?.customer_gst_number ?? '') === gstString
      );
      if (match?.state_id) {
        return String(match.state_id);
      }
    } else {
      const taxInfoList = Array.isArray(this.currentSubsidiaryInfo?.tax_information)
        ? this.currentSubsidiaryInfo.tax_information
        : [];
      const match = taxInfoList.find(
        (tax: any) => !tax?.is_deleted && String(tax?.gst_number ?? '') === gstString
      );
      if (match?.state_id) {
        return String(match.state_id);
      }

      // Fallback: search across cached subsidiaries if current one not set
      for (const subsidiary of this.subsidiariesData || []) {
        const infoList = Array.isArray(subsidiary?.tax_information) ? subsidiary.tax_information : [];
        const fallbackMatch = infoList.find(
          (tax: any) => !tax?.is_deleted && String(tax?.gst_number ?? '') === gstString
        );
        if (fallbackMatch?.state_id) {
          return String(fallbackMatch.state_id);
        }
      }
    }

    return this.extractStateCodeFromGst(gstString);
  }

  private calculateTaxTypeFromGstNumbers(): string | null {
    if (!this.customerForm) {
      return null;
    }

    const customerGstKey = this.getFieldKey('customer_gst_number');
    const subsidiaryGstKey = this.getFieldKey('subsidiary_gst_number');

    const customerGst = this.customerForm.get(customerGstKey)?.value;
    const subsidiaryGst = this.customerForm.get(subsidiaryGstKey)?.value;

    if (!customerGst || !subsidiaryGst) {
      return null;
    }

    const customerState = this.getStateIdFromGstNumber(customerGst, 'customer');
    const subsidiaryState = this.getStateIdFromGstNumber(subsidiaryGst, 'subsidiary');

    if (!customerState || !subsidiaryState) {
      return null;
    }

    return customerState === subsidiaryState ? 'CGST+SGST' : 'IGST';
  }

  private updateTaxTypeInAllTables(taxType: string | null): void {
    if (!taxType || !this.customerForm) {
      return;
    }
    const taxTypeKey = this.getFieldKey('tax_type');
    const tableKeys = Array.from(this.tableFormArrays.keys());

    tableKeys.forEach((tableKey) => {
      const formArray = this.getTableFormArray(tableKey);
      if (formArray && formArray instanceof FormArray) {
        formArray.controls.forEach((rowControl) => {
          if (rowControl instanceof FormGroup) {
            const control = rowControl.get(taxTypeKey);
            if (control) {
              control.setValue(taxType, { emitEvent: false });
            }
          }
        });
      }
    });
  }

  private calculateAndUpdateTaxType(): void {
    const taxType = this.calculateTaxTypeFromGstNumbers();
    this.calculatedTaxType = taxType;
    if (taxType) {
      this.updateTaxTypeInAllTables(taxType);
    }
  }

  // Handle field change events emitted by app-dynamic-form
  async onChildFieldChanged(event: any) {
    if (!event || !event.key) return;
    const rawKey = String(event.key || '');
    const key = rawKey.toLowerCase();
    const value = event?.value ?? event;

    // Handle audit log filters (date/search/audit fields) in view mode
    if (
      this.mode === 'view' &&
      (key.includes('date') || key.includes('filter') || key.includes('search') || key.includes('audit'))
    ) {
      await this.handleAuditLogFiltering(rawKey, value, event);
    }

    switch (key) {
      case 'client_id':
        if (value && typeof value === 'object' && value.label) {
           const clientControl = this.customerForm?.get(this.getFieldKey('client_id'));
           if (clientControl) {
             clientControl.setValue(value, { emitEvent: false });
           }
        }
        await this.handleClientChange(value);
        break;
      case 'form_template_id':
        await this.onTemplateChange(value);
        break;
      case 'subsidiary_id':
        await this.handleSubsidiaryChange(value, false);
        break;
      case 'ship_to':
        this.handleShipToSelection(value);
        break;
      case 'bill_to':
        this.handleBillToSelection(value);
        break;
      case 'location':
        // Handle location change and update subsidiary GST number
        this.handleLocationChange(value, false);
        break;
      case 'tax_details_override':
        const shouldEnable = this.isTruthyValue(value);
        this.setTaxOverrideDependentFieldsState(shouldEnable);
        break;
      case 'customer_gst_number':
      case 'subsidiary_gst_number':
        this.calculateAndUpdateTaxType();
        break;
      default:
        // no-op for other fields
        break;
    }
  }

  /**
   * Handle autocomplete search for primary form fields
   */
  onPrimaryFormSearchQuery(event: { fieldName: string; query: string; field?: FieldConfig }): void {
    if (!event?.fieldName) {
      return;
    }

    if (event.fieldName === 'client_id') {
      // Handle async search for client
      this.handleClientSearch(event.query ?? '').catch(error => {
        console.error('Error in client search:', error);
      });
    }
  }


  private getFormValue(source: any, fieldName: string, fallback?: any): any {
    if (!this.customerForm) {
      return fallback;
    }

    const key = this.getFieldKey(fieldName);
    if (source && Object.prototype.hasOwnProperty.call(source, key)) {
      const value = source[key];
      if (value !== undefined) {
        return value;
      }
    }

    if (source && Object.prototype.hasOwnProperty.call(source, fieldName)) {
      const direct = source[fieldName];
      if (direct !== undefined) {
        return direct;
      }
    }

    const control = this.customerForm.get(key);
    if (control) {
      const controlValue =
        typeof (control as any).getRawValue === 'function'
          ? (control as any).getRawValue()
          : control.value;
      if (controlValue !== undefined) {
        return controlValue;
      }
    }

    return fallback;
  }

  private getRowValue(row: any, fieldName: string, fallback?: any): any {
    if (!row) return fallback;
    const key = this.getFieldKey(fieldName);
    if (Object.prototype.hasOwnProperty.call(row, key) && row[key] !== undefined) {
      return row[key];
    }
    if (Object.prototype.hasOwnProperty.call(row, fieldName) && row[fieldName] !== undefined) {
      return row[fieldName];
    }
    return fallback;
  }

  private normalizeIdValue(value: any): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'object') {
      const candidate =
        value.id ??
        value.value ??
        value.client_id ??
        value._id ??
        value.uuid ??
        value.code ??
        value.name ??
        null;
      return candidate !== null && candidate !== undefined ? String(candidate) : null;
    }

    return String(value);
  }

  private toNumberValue(value: any, fallback: number | null = null): number | null {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }
    if (typeof value === 'number') {
      return isNaN(value) ? fallback : value;
    }
    const numeric = Number(value);
    return isNaN(numeric) ? fallback : numeric;
  }

  private toIsoString(value: any): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value.toISOString();
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return null;
      }
      const parsed = new Date(trimmed);
      if (!isNaN(parsed.getTime()) && !trimmed.includes('T')) {
        return parsed.toISOString();
      }
      return trimmed;
    }
    return null;
  }

  private toStringValue(value: any): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length ? trimmed : null;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    if (typeof value === 'object') {
      if (value.label) return String(value.label);
      if (value.name) return String(value.name);
      if (value.value) return String(value.value);
      if (value.id) return String(value.id);
    }

    return null;
  }

  private getUnitName(unitId: string | null): string | null {
    if (!unitId || !this.unitsOptions || this.unitsOptions.length === 0) {
      return null;
    }

    const matchedUnit = this.unitsOptions.find((opt: any) => {
      const optId = String(opt.id ?? opt.value ?? '');
      const optCode = String(opt.code ?? '');
      const searchId = String(unitId);
      return optId === searchId || optCode === searchId;
    });

    if (matchedUnit) {
      return matchedUnit.name ?? matchedUnit.display_name ?? matchedUnit.label ?? null;
    }

    return null;
  }

  private pruneNullish<T extends Record<string, any>>(obj: T, removeEmptyString = false): T {
    Object.keys(obj).forEach(key => {
      const value = obj[key];
      if (
        value === undefined ||
        value === null ||
        (removeEmptyString && typeof value === 'string' && value.trim() === '')
      ) {
        delete obj[key];
      }
    });
    return obj;
  }

  private getTableArray(key: string): FormArray | null {
    const normalizedKey = this.getFieldKey(key);
    const fromMap = this.tableFormArrays.get(normalizedKey);
    if (fromMap instanceof FormArray) {
      return fromMap;
    }

    const direct = this.customerForm?.get(normalizedKey);
    if (direct instanceof FormArray) {
      return direct;
    }

    return null;
  }

  private isRowMarkedDeleted(rowGroup: FormGroup): boolean {
    if (!rowGroup) return false;
    const value = rowGroup.get('is_deleted')?.value;
    return value === true || value === 1 || value === '1' || value === 'true';
  }

  private buildOrderItems(): any[] {
    const itemsArray = this.getTableArray('sub_tab_3');
    if (!itemsArray || itemsArray.length === 0) {
      return [];
    }
    
    // Get items table config to access field metadata
    const itemsConfig = this.getItemsTableConfig();
    const itemFields = itemsConfig?.fields || [];
    
    const items: any[] = [];
    itemsArray.controls.forEach(control => {
      if (!(control instanceof FormGroup)) {
        return;
      }

      const raw = typeof control.getRawValue === 'function' ? control.getRawValue() : control.value;
      const itemId = this.normalizeIdValue(
        this.getRowValue(raw, 'item_id') ?? this.getRowValue(raw, 'item')
      );
      const orderItemId = this.normalizeIdValue(
        this.getRowValue(raw, 'id')
      );
      if (!itemId && !orderItemId) {
        return;
      }

      // Separate custom fields from regular fields
      const itemCustomFields: any = {};
      const item: any = {
        id: orderItemId,
        item_id: itemId,
        item_name: this.toStringValue(this.getRowValue(raw, 'item_name')),
        item_type: this.toStringValue(this.getRowValue(raw, 'item_type')),
        quantity: this.toNumberValue(this.getRowValue(raw, 'quantity')),
        foc: this.getRowValue(raw, 'foc'),
        pack: this.toNumberValue(this.getRowValue(raw, 'pack')),
        scheme: this.toNumberValue(this.getRowValue(raw, 'scheme')),
        description: this.toStringValue(this.getRowValue(raw, 'description')),
        unit_id: this.toStringValue(this.getRowValue(raw, 'units')),
        unit_name: this.getUnitName(this.toStringValue(this.getRowValue(raw, 'units'))),
        last_order_price: this.toNumberValue(this.getRowValue(raw, 'last_purchase_price')),
        current_stock: this.toNumberValue(this.getRowValue(raw, 'current_stock')),
        mrp: this.toNumberValue(this.getRowValue(raw, 'mrp')),
        rate: this.toNumberValue(this.getRowValue(raw, 'rate')),
        gross_amount: this.toNumberValue(this.getRowValue(raw, 'gross_amount')),
        tax_rate: this.toNumberValue(this.getRowValue(raw, 'tax_rate')),
        tax_type: this.toStringValue(this.getRowValue(raw, 'tax_type')),
        tax_amount: this.toNumberValue(this.getRowValue(raw, 'tax_amount')),
        net_amount: this.toNumberValue(this.getRowValue(raw, 'net_amount')),
        discount_percentage: this.toNumberValue(this.getRowValue(raw, 'discount_percentage')),
        discount_amount: this.toNumberValue(this.getRowValue(raw, 'discount_amount')),
        total_amount: this.toNumberValue(this.getRowValue(raw, 'total_amount')),
        hsn_code:
          this.toStringValue(this.getRowValue(raw, 'hsn')) ??
          this.toStringValue(this.getRowValue(raw, 'hsn_code')),
        uqc: this.toStringValue(this.getRowValue(raw, 'uqc')),
        line_of_business: this.toStringValue(this.getRowValue(raw, 'line_of_business')),
        is_deleted: this.getRowValue(raw, 'is_deleted')
      };

      // Extract custom fields from the row based on field configuration
      itemFields.forEach((field: FieldConfig) => {
        if (field.is_custom_field) {
          const value = this.getRowValue(raw, field.field_name);
          
          if (value !== undefined && value !== null && value !== '') {
            // Transform value based on data type
            let transformedValue: any;
            
            if (field.data_type === 'date' || field.data_type === 'datetime') {
              transformedValue = this.toIsoString(value);
            } else if (field.data_type === 'number' || field.data_type === 'integer' || 
                       field.data_type === 'float' || field.data_type === 'double' || 
                       field.data_type === 'bigint') {
              transformedValue = this.toNumberValue(value);
            } else if (field.data_type === 'boolean' || field.data_type === 'tinyint') {
              transformedValue = this.normalizeBoolean(value);
            } else {
              transformedValue = this.toStringValue(value);
            }
            
            if (transformedValue !== null && transformedValue !== undefined && transformedValue !== '') {
              itemCustomFields[field.field_name] = transformedValue;
            }
          }
        }
      });

      // Prune nullish values from regular item fields
      const prunedItem = this.pruneNullish(item, true);
      
      // Add custom_fields object if there are any custom fields
      if (Object.keys(itemCustomFields).length > 0) {
        prunedItem.custom_fields = itemCustomFields;
      }

      items.push(prunedItem);
    });

    return items;
  }

  private buildUserNotes(): any[] {
    const userNotesConfig = this.getUserNotesTableConfig();
    if (!userNotesConfig) {
      return [];
    }

    const notesArray = this.getTableFormArray(userNotesConfig.key);
    if (!notesArray || notesArray.length === 0) {
      return [];
    }

    const noteFields = userNotesConfig.fields || [];
    const notes: any[] = [];

    notesArray.controls.forEach(control => {
      if (!(control instanceof FormGroup)) {
        return;
      }

      const raw = typeof control.getRawValue === 'function' ? control.getRawValue() : control.value;
      const noteId = this.normalizeIdValue(this.getRowValue(raw, 'id'));
      
      // Check if the row has any meaningful data
      const title = this.toStringValue(this.getRowValue(raw, 'title'));
      const memo = this.toStringValue(this.getRowValue(raw, 'memo'));
      
      // Skip empty rows (except when updating existing notes)
      if (!noteId && !title && !memo) {
        return;
      }

      // Separate custom fields from regular fields
      const noteCustomFields: any = {};
      
      // Get time value as ISO string (datetime format)
      const timeValue = this.toIsoString(this.getRowValue(raw, 'time'));
      
      const note: any = {
        id: noteId,
        title: title,
        memo: memo,
        date: this.toIsoString(this.getRowValue(raw, 'date')),
        type: this.toStringValue(this.getRowValue(raw, 'type')),
        direction: this.toStringValue(this.getRowValue(raw, 'direction')),
        is_deleted: this.getRowValue(raw, 'is_deleted')
      };
      
      // Only add time if it has a valid value
      if (timeValue) {
        note.time = timeValue;
      }
      // Extract custom fields from the row based on field configuration
      noteFields.forEach((field: FieldConfig) => {
        if (field.is_custom_field) {
          const value = this.getRowValue(raw, field.field_name);
          
          if (value !== undefined && value !== null && value !== '') {
            // Transform value based on data type
            let transformedValue: any;
            
            if (field.data_type === 'date' || field.data_type === 'datetime') {
              transformedValue = this.toIsoString(value);
            } else if (field.data_type === 'number' || field.data_type === 'integer' || 
                       field.data_type === 'float' || field.data_type === 'double' || 
                       field.data_type === 'bigint') {
              transformedValue = this.toNumberValue(value);
            } else if (field.data_type === 'boolean' || field.data_type === 'tinyint') {
              transformedValue = this.normalizeBoolean(value);
            } else {
              transformedValue = this.toStringValue(value);
            }
            
            if (transformedValue !== null && transformedValue !== undefined && transformedValue !== '') {
              noteCustomFields[field.field_name] = transformedValue;
            }
          }
        }
      });

      // Prune nullish values from regular note fields
      const prunedNote = this.pruneNullish(note, true);
      
      // Add custom_fields object if there are any custom fields
      if (Object.keys(noteCustomFields).length > 0) {
        prunedNote.custom_fields = noteCustomFields;
      }

      notes.push(prunedNote);
    });

    return notes;
  }

  private extractCustomFields(formValues: any): Record<string, any> {
    const custom: Record<string, any> = {};
    (this.customFields ?? []).forEach((field: FieldConfig) => {
      const key = this.getFieldKey(field.field_name);
      const value =
        (formValues && Object.prototype.hasOwnProperty.call(formValues, key)
          ? formValues[key]
          : undefined) ?? this.customerForm?.get(key)?.value;

      if (value !== undefined && value !== null && value !== '') {
        custom[field.field_name] = value;
      }
    });

    return custom;
  }

  private syncTaxOverrideDependentFieldsFromForm(): void {
    if (!this.customerForm) {
      return;
    }

    const controlKey = this.getFieldKey('tax_details_override');
    const overrideControl = this.customerForm.get(controlKey);
    if (!overrideControl) {
      return;
    }

    const shouldEnable = this.isTruthyValue(overrideControl.value);
    this.setTaxOverrideDependentFieldsState(shouldEnable);
  }

  private isTruthyValue(value: any): boolean {
    if (value === true || value === 1) {
      return true;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'y';
    }

    return false;
  }

  private setTaxOverrideDependentFieldsState(shouldEnable: boolean): void {
    if (!this.customerForm) {
      return;
    }

    const canEnable = shouldEnable && this.mode !== 'view';

    this.taxOverrideDependentFields.forEach(fieldName => {
      const controlKey = this.getFieldKey(fieldName);
      const control = this.customerForm?.get(controlKey);

      if (!control) {
        return;
      }

      if (canEnable) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    });
  }
}
